/*
 * Raw Dawg Quiz V3 decision core.
 *
 * This file intentionally contains no DOM, storage, network, cart, or provider
 * behavior. The presentation layer owns those concerns and must call
 * validateCartLines immediately before any /cart/add request.
 */
(function rawDawgQuizCoreModule(root, factory) {
  const api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  if (root) root.RawDawgQuizCore = api;
}(typeof window !== "undefined" ? window : (typeof globalThis !== "undefined" ? globalThis : null), function createRawDawgQuizCore() {
  "use strict";

  // This value is also the Klaviyo schema_version. Keep it intentionally
  // simple so event and profile projections can be reconciled without a
  // second translation layer.
  const SCHEMA_VERSION = "3.0";
  const QUIZ_VERSION = "3.4";
  const CALCULATION_REVISION = "raw-fit-v3.4-2026-10-breed-rules-explicit-condition";
  const TOTAL_STEPS = 9;
  const DEFAULT_AS_OF = () => new Date().toISOString().slice(0, 10);
  const KNOWN_PROTEINS = ["beef", "turkey", "salmon"];
  const BOX_SIZES = [8, 14, 30];
  // Keep the packaging decision aligned with the live Feeding Calculator.
  // The smaller tolerance is only for binary floating-point noise; it does
  // not relax a real customer-facing quantity boundary.
  const LOWER_ORDER_TOLERANCE = 0.15;
  const FLOATING_POINT_TOLERANCE = 0.000001;
  const SUPPORTED_SUBSCRIPTION_CADENCE_WEEKS = [2, 4];
  const ROLE_PROTEINS = Object.freeze({
    beef: ["beef"],
    turkey: ["turkey"],
    salmon: ["turkey", "salmon"],
    variety: ["beef", "turkey", "salmon"],
  });
  // These decks are copied exactly from page.ingredients.json. They are the
  // reviewed source for protein compatibility; product titles are never used
  // as a substitute for the actual named ingredients.
  const FORMULA_CATALOG = Object.freeze({
    beef: Object.freeze({
      role: "beef",
      handle: "raw-dawg-beef-box",
      title: "Beef & Green Tripe",
      ingredients: "Beef, beef heart, beef liver, beef kidney, beef spleen, ground bone, raw unbleached beef green tripe.",
    }),
    turkey: Object.freeze({
      role: "turkey",
      handle: "raw-dawg-turkey-box",
      title: "Pasture-Raised Turkey",
      ingredients: "Pasture-Raised Turkey Thighs, Heart, Gizzard, Neck Bone, and Liver.",
    }),
    salmon: Object.freeze({
      role: "salmon",
      handle: "raw-dawg-turkey-salmon-box",
      title: "Turkey & Salmon",
      ingredients: "Wild-Caught Salmon with skin and head, Pasture-Raised Turkey Thighs, Heart, Gizzard, Neck Bone, and Liver.",
    }),
  });
  const FORMULA_ROLES = Object.freeze(["beef", "turkey", "salmon"]);
  const SIZE_PROFILES = Object.freeze({
    Toy: { minAge: 12, seniorAge: 11 },
    Small: { minAge: 12, seniorAge: 11 },
    Medium: { minAge: 12, seniorAge: 8 },
    Large: { minAge: 12, seniorAge: 7 },
    Giant: { minAge: 18, seniorAge: 6 },
    Unknown: { minAge: 18, seniorAge: 6 },
  });
  const BREED_IDENTITY_ALIASES = Object.freeze({
    "anatolian shepherd dog": "Anatolian Shepherd",
    "mastiff": "Mastiff (English)",
    "st. bernard": "Saint Bernard",
    "st bernard": "Saint Bernard",
    "cocker spaniel": "Cocker Spaniel (American)",
  });
  const WATCH_FLAG_PRIORITY = ["copper", "urate", "fat", "epi"];
  // Original recommendation workbook Breed Database D/I/K, approved Oct 10.
  // This is a brand exclusion policy, not a clinical nutrient threshold.
  // New breeds require a reviewed policy change, not an inferred diagnosis.
  const FAT_RESTRICTED_BREEDS = new Set([
    "Toy Poodle", "Yorkshire Terrier", "Dachshund", "Miniature Schnauzer",
    "Shetland Sheepdog", "Toy/Mini Poodle (Miniature)", "Beagle",
    "Cocker Spaniel (American)", "English Cocker Spaniel", "Standard Schnauzer",
    "Briard", "Collie (Rough)", "Collie (Smooth)", "Doberman Pinscher",
    "Rottweiler", "Standard Poodle",
  ]);
  const CONDITION_ALIASES = Object.freeze({
    antibiotics: "antibiotics_current",
    antibiotics_current: "antibiotics_current",
    antibiotics_now: "antibiotics_current",
    antibiotics_recent: "antibiotics_recent",
    antibiotics_within_14_days: "antibiotics_recent",
    antibiotics_finished_within_14_days: "antibiotics_recent",
    pancreatitis_active: "pancreatitis_active_recovering",
    pancreatitis_recent: "pancreatitis_active_recovering",
    pancreatitis_recovering: "pancreatitis_active_recovering",
    pancreatitis_active_recovering: "pancreatitis_active_recovering",
    pancreatitis_stable: "pancreatitis_stable_history",
    pancreatitis_history: "pancreatitis_stable_history",
    pancreatitis_stable_history: "pancreatitis_stable_history",
    gi_active: "gi_active_recovering",
    gi_recent: "gi_active_recovering",
    gi_recovering: "gi_active_recovering",
    gi_active_recovering: "gi_active_recovering",
    chronic_gi: "gi_active_recovering",
    chronic_gi_active: "gi_active_recovering",
    chronic_gi_recent: "gi_active_recovering",
    ibd: "gi_active_recovering",
    ibd_active: "gi_active_recovering",
    ibd_recent: "gi_active_recovering",
    gi_stable: "gi_stable_history",
    gi_history: "gi_stable_history",
    gi_stable_history: "gi_stable_history",
    chronic_gi_stable: "gi_stable_history",
    ibd_stable: "gi_stable_history",
    immune: "immune_immunosuppression",
    immunocompromised: "immune_immunosuppression",
    immune_disorder: "immune_immunosuppression",
    immune_immunosuppression: "immune_immunosuppression",
    pregnant: "pregnant",
    nursing: "nursing",
    other_watch: "other_watch",
  });
  const WAIT_CONDITION_FLAGS = new Set([
    "antibiotics_current",
    "antibiotics_recent",
    "pancreatitis_active_recovering",
    "gi_active_recovering",
    "immune_immunosuppression",
    "pregnant",
    "nursing",
  ]);
  const CONDITIONAL_CONDITION_FLAGS = new Set([
    "pancreatitis_stable_history",
    "gi_stable_history",
    "other_watch",
  ]);
  const RECOMMENDATION_ROLE_ORDER = Object.freeze(["beef", "turkey", "salmon", "variety"]);

  /*
   * Reviewed only against the AKC group list revised 2026-03-10. The JSON
   * sidecar records the source and aliases. Unlisted, Mixed Breed, and Unknown
   * values deliberately remain ungrouped; group membership is never inferred
   * from size, activity, examples, or a free-form breedGroup field.
   */
  const REVIEWED_BREED_GROUPS = Object.freeze({
    "Akita": "Working",
    "Alaskan Malamute": "Working",
    "Anatolian Shepherd": "Working",
    "Bernese Mountain Dog": "Working",
    "Black Russian Terrier": "Working",
    "Boxer": "Working",
    "Bullmastiff": "Working",
    "Cane Corso": "Working",
    "Doberman Pinscher": "Working",
    "Dogue de Bordeaux": "Working",
    "Great Dane": "Working",
    "Great Pyrenees": "Working",
    "Greater Swiss Mountain Dog": "Working",
    "Komondor": "Working",
    "Kuvasz": "Working",
    "Leonberger": "Working",
    "Mastiff (English)": "Working",
    "Neapolitan Mastiff": "Working",
    "Newfoundland": "Working",
    "Portuguese Water Dog": "Working",
    "Rottweiler": "Working",
    "Saint Bernard": "Working",
    "Samoyed": "Working",
    "Siberian Husky": "Working",
    "Standard Schnauzer": "Working",
    "Tibetan Mastiff": "Working",
    "Brittany": "Sporting",
    "Chesapeake Bay Retriever": "Sporting",
    "Cocker Spaniel (American)": "Sporting",
    "Curly-Coated Retriever": "Sporting",
    "English Cocker Spaniel": "Sporting",
    "English Setter": "Sporting",
    "English Springer Spaniel": "Sporting",
    "Flat-Coated Retriever": "Sporting",
    "German Shorthaired Pointer": "Sporting",
    "German Wirehaired Pointer": "Sporting",
    "Golden Retriever": "Sporting",
    "Gordon Setter": "Sporting",
    "Irish Setter": "Sporting",
    "Labrador Retriever": "Sporting",
    "Nova Scotia Duck Tolling Retriever": "Sporting",
    "Pointer": "Sporting",
    "Vizsla": "Sporting",
    "Weimaraner": "Sporting",
    "Wirehaired Pointing Griffon": "Sporting",
  });

  /*
   * These are safer/replacement strings from the accepted V2 Interstitials
   * sheet, never the Spec Copy column. sourceStatus documents the original
   * claim disposition; its risky source claim is not present in this file.
   */
  const INTERSTITIALS = Object.freeze([
    { id: "RT-01-D", step: 1, type: "default", condition: "all", status: "Rewrite", copy: "[dog_name] is built for real food. Let's use their profile to find a practical place to start." },
    { id: "RT-02-D", step: 2, type: "default", condition: "default", status: "Rewrite", copy: "Dogs still carry many of the traits of their wild relatives. Their breed profile helps us choose a practical starting path." },
    { id: "RT-02-FAT", step: 2, type: "override", condition: "breed_flag_fat", status: "Blocked", copy: "[Breed] can have breed-related feeding considerations. This quiz will pause the product recommendation until the appropriate feeding path is confirmed." },
    { id: "RT-02-URATE", step: 2, type: "override", condition: "breed_flag_urate", status: "Evidence gate", copy: "[Breed] can have breed-specific purine considerations. This quiz will pause the standard recommendation until the appropriate feeding path is confirmed." },
    { id: "RT-02-COPPER", step: 2, type: "override", condition: "breed_flag_copper", status: "Blocked", copy: "[Breed] can have breed-specific copper considerations. This quiz will pause the standard recommendation until the appropriate feeding path is confirmed." },
    { id: "RT-02-GIANT", step: 2, type: "override", condition: "size_giant", status: "Rewrite", copy: "Giant breeds have different growth and portion needs. We'll use [dog_name]'s age and weight to keep the starting plan conservative." },
    { id: "RT-02-SMALL", step: 2, type: "override", condition: "size_toy_or_small", status: "Rewrite", copy: "Small dogs can have high energy needs relative to their size. Every ounce matters, so we'll keep the portion and box size practical." },
    { id: "RT-03-D", step: 3, type: "default", condition: "adult_not_senior", status: "Remove", copy: "Dogs digest food differently than people do. Safe handling and a gradual transition still matter with every raw bowl." },
    { id: "RT-03-PUPPY", step: 3, type: "override", condition: "puppy", status: "Review", copy: "[dog_name] is still growing. Growing dogs need carefully controlled mineral balance, and our current formulas are built for adults. We'll show the estimated eligibility date from the information provided." },
    { id: "RT-03-SENIOR", step: 3, type: "override", condition: "senior", status: "Rewrite", copy: "Senior dogs deserve a slower, more observant transition. We'll start conservatively and adjust from body condition and routine." },
    { id: "RT-04-D", step: 4, type: "default", condition: "goal_maintain", status: "Rewrite", copy: "We calculate a practical starting amount in daily ounces from [dog_name]'s weight, goal, and activity. Then you adjust by observation." },
    { id: "RT-04-LOSE", step: 4, type: "override", condition: "goal_lose", status: "Rewrite", copy: "For a trim-down goal, we'll calculate from the target weight and begin with a conservative portion." },
    { id: "RT-04-GAIN", step: 4, type: "override", condition: "goal_gain", status: "Rewrite", copy: "For a build-up goal, we'll calculate from the target weight and use a measured starting amount." },
    { id: "RT-05-D", step: 5, type: "default", condition: "activity_moderate", status: "Rewrite", copy: "Activity changes the starting percentage. We'll use [dog_name]'s normal routine to keep the portion realistic." },
    { id: "RT-05-HIGH", step: 5, type: "override", condition: "activity_high", status: "Remove", copy: "High-output dogs can need more energy. We'll adjust the starting estimate modestly and recommend monitoring body condition." },
    { id: "RT-05-LOW", step: 5, type: "override", condition: "activity_low", status: "Review", copy: "Even low-key dogs deserve a thoughtful bowl. We'll dial the starting portion down, not the care." },
    { id: "RT-06-REG", step: 6, type: "default", condition: "raw_regularly", status: "Review", copy: "You already know the routine. Now let's make sure the starting portion and blend fit [dog_name]." },
    { id: "RT-06-SOME", step: 6, type: "override", condition: "raw_sometimes", status: "Rewrite", copy: "A topper is a familiar starting point. We'll show a gradual path toward the calculated amount if the adult route is appropriate." },
    { id: "RT-06-NEW", step: 6, type: "override", condition: "raw_not_yet", status: "Rewrite", copy: "We'll build [dog_name] a gradual 7–10 day starting ramp and ask you to slow down if the routine becomes unsettled." },
    { id: "RT-07-D", step: 7, type: "default", condition: "no_avoidances", status: "Remove", copy: "With no known avoidances, more than one eligible protein can stay in consideration for [dog_name]'s plan." },
    { id: "RT-07-AVOID", step: 7, type: "override", condition: "has_avoidance", status: "Blocked", copy: "We'll screen the available blends against the proteins you selected. If none fit the approved route, we won't recommend a box." },
    { id: "RT-08-D", step: 8, type: "default", condition: "health_none", status: "Rewrite", copy: "None of the listed watch items were selected. [dog_name] can continue to the standard adult decision path." },
    { id: "RT-08-FLAG", step: 8, type: "override", condition: "health_flag", status: "Review", copy: "Thanks for telling us. This answer changes the path, and some routes will pause the box recommendation." },
    { id: "RT-09-D", step: 9, type: "default", condition: "outcome_default", status: "Rewrite", copy: "Building [dog_name]'s plan from the answers you provided: breed profile, age, weight, activity, goals, and watch items." },
    { id: "RT-09-DIGEST", step: 9, type: "override", condition: "outcome_digestion", status: "Remove", copy: "You selected digestion. The plan will keep the transition gradual and the starting portion measured." },
    { id: "RT-09-COAT", step: 9, type: "override", condition: "outcome_coat", status: "Evidence gate", copy: "You selected skin and coat support. If salmon remains compatible, the V3 route can prioritize the Turkey & Salmon blend." },
    { id: "RT-09-INTEREST", step: 9, type: "override", condition: "outcome_interest", status: "Rewrite", copy: "You selected mealtime interest. We'll keep protein eligibility and the feeding route ahead of preference." },
  ]);

  const INTERSTITIAL_BY_ID = Object.freeze(INTERSTITIALS.reduce((all, item) => {
    all[item.id] = item;
    return all;
  }, {}));

  // V3.1 restores a short Raw Truth after every question. The three chapter
  // records remain as named pacing and analytics checkpoints after Q3, Q6,
  // and Q9, while the UI selects one INTERSTITIALS entry after Q1-Q9.
  const CHAPTER_CARDS = Object.freeze([
    {
      id: "know_your_dawg",
      afterQuestion: 3,
      eyebrow: "Know Your Dawg",
      title: "The basics are in the bowl.",
      copy: "Age, breed, and current size help us keep the next step practical for [dog_name].",
    },
    {
      id: "check_the_route",
      afterQuestion: 6,
      eyebrow: "Check the Route",
      title: "Now we match the path to the routine.",
      copy: "Raw experience, protein fit, and a few guardrails tell us whether [dog_name] is ready now or needs a later plan.",
    },
    {
      id: "build_the_bowl",
      afterQuestion: 9,
      eyebrow: "Build the Bowl",
      title: "One profile. One practical starting plan.",
      copy: "We are turning [dog_name]'s answers into a feeding amount, four-week box plan, and the right next move.",
    },
  ]);
  const CHAPTER_CARD_BY_QUESTION = Object.freeze(CHAPTER_CARDS.reduce((all, card) => {
    all[card.afterQuestion] = card;
    return all;
  }, {}));

  const CANONICAL_VARIANT_COMPOSITIONS = Object.freeze({
    "all beef": ["beef"],
    "all turkey": ["turkey"],
    "all turkey & salmon": ["turkey", "salmon"],
    "50% beef / 50% turkey": ["beef", "turkey"],
    "50% beef / 50% turkey & salmon": ["beef", "turkey", "salmon"],
    "50% turkey / 50% beef": ["beef", "turkey"],
    "50% turkey / 50% turkey & salmon": ["turkey", "salmon"],
    "50% turkey & salmon / 50% beef": ["beef", "turkey", "salmon"],
    "50% turkey & salmon / 50% turkey": ["turkey", "salmon"],
    "8 lb beef / 6 lb turkey": ["beef", "turkey"],
    "8 lb beef / 6 lb turkey & salmon": ["beef", "turkey", "salmon"],
    "8 lb turkey / 6 lb beef": ["beef", "turkey"],
    "8 lb turkey / 6 lb turkey & salmon": ["turkey", "salmon"],
    "8 lb turkey & salmon / 6 lb beef": ["beef", "turkey", "salmon"],
    "8 lb turkey & salmon / 6 lb turkey": ["turkey", "salmon"],
    "10 lb beef / 10 lb turkey / 10 lb turkey & salmon": ["beef", "turkey", "salmon"],
  });
  /* Actual 8/14/30 Variety options: balanced pairs at 8 lb, ordered 8+6
   * chub pairs at 14 lb, and all three proteins only at 30 lb. This is a composition map, not a product-handle
   * assumption; the final cart gate still validates the selected variant. */
  const VARIETY_OPTION_LABELS_BY_SIZE = Object.freeze({
    8: [
      "50% beef / 50% turkey & salmon",
      "50% turkey & salmon / 50% beef",
      "50% beef / 50% turkey",
      "50% turkey / 50% beef",
      "50% turkey / 50% turkey & salmon",
      "50% turkey & salmon / 50% turkey",
    ],
    14: [
      "8 lb beef / 6 lb turkey & salmon",
      "8 lb turkey & salmon / 6 lb beef",
      "8 lb beef / 6 lb turkey",
      "8 lb turkey / 6 lb beef",
      "8 lb turkey / 6 lb turkey & salmon",
      "8 lb turkey & salmon / 6 lb turkey",
    ],
    30: ["10 lb beef / 10 lb turkey / 10 lb turkey & salmon"],
  });

  const BLOCKED_COPY_PATTERNS = [
    /whole[-\s]?prey\s+complete/i,
    /complete\s+and\s+balanced/i,
    /60%\s+less\s+stool/i,
    /immun(?:e|ity)/i,
    /microbiome/i,
    /wolf/i,
    /fat\s+triggers\s+pancreatitis/i,
    /raw\s+bacteria.*benefit/i,
    /stomach\s+acid.*(destroy|neutraliz).*bacteria/i,
    /guarantee[ds]?\b/i,
    /exact\s+(portion|ounce|amount)/i,
    /leanest|lowest\s+fat|most\s+digestible/i,
  ];

  function isPlainObject(value) {
    return Boolean(value) && typeof value === "object" && !Array.isArray(value);
  }

  function hasOwn(object, key) {
    return Boolean(object) && Object.prototype.hasOwnProperty.call(object, key);
  }

  function asArray(value) {
    if (Array.isArray(value)) return value;
    if (value === undefined || value === null || value === "") return [];
    return [value];
  }

  function toText(value) {
    return String(value === undefined || value === null ? "" : value).trim();
  }

  function canonicalValue(value) {
    return toText(value).toLowerCase().replace(/\s+/g, "_");
  }

  function unique(values) {
    return [...new Set(values)];
  }

  function clone(value) {
    return JSON.parse(JSON.stringify(value));
  }

  function normaliseSelection(values, exclusiveValue) {
    const exclusive = canonicalValue(exclusiveValue || "none");
    const normalised = unique(asArray(values)
      .map(canonicalValue)
      .filter(Boolean));
    // A persisted/conflicting ["none", "pancreatitis_active"] state must
    // never erase a real avoidance or medical flag. UI selection keeps none
    // exclusive, but migration and calculation deliberately retain concrete
    // values if a malformed older state contains both.
    const concrete = normalised.filter((value) => value !== exclusive);
    return concrete.length ? concrete : (normalised.includes(exclusive) ? [exclusive] : []);
  }

  /**
   * Toggle a value in a multi-select set where `none` is mutually exclusive.
   * This is deliberately generic and is shared by proteins, health, and goals.
   */
  function normalizeExclusiveSelection(currentValues, clickedValue, exclusiveValue) {
    const exclusive = canonicalValue(exclusiveValue || "none");
    const values = normaliseSelection(currentValues, exclusive);
    const clicked = canonicalValue(clickedValue);
    if (!clicked) return values;
    if (clicked === exclusive) return values.includes(exclusive) ? [] : [exclusive];
    const withoutExclusive = values.filter((value) => value !== exclusive);
    return withoutExclusive.includes(clicked)
      ? withoutExclusive.filter((value) => value !== clicked)
      : [...withoutExclusive, clicked];
  }

  function datePartsFromIso(value) {
    const match = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(toText(value));
    if (!match) return null;
    return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
  }

  function monthPartsFromIso(value) {
    const exact = /^([0-9]{4})-([0-9]{2})$/.exec(toText(value));
    if (exact) return { year: Number(exact[1]), month: Number(exact[2]) };
    const slash = /^([0-9]{1,2})\/([0-9]{4})$/.exec(toText(value));
    if (slash) return { year: Number(slash[2]), month: Number(slash[1]) };
    return null;
  }

  function birthdayPartsFromSlash(value) {
    const match = /^([0-9]{1,2})\/([0-9]{1,2})\/([0-9]{4})$/.exec(toText(value));
    if (!match) return null;
    return { year: Number(match[3]), month: Number(match[1]), day: Number(match[2]) };
  }

  function isLeapYear(year) {
    return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  }

  function daysInMonth(year, month) {
    if (month === 2) return isLeapYear(year) ? 29 : 28;
    return [4, 6, 9, 11].includes(month) ? 30 : 31;
  }

  function isRealDate(parts) {
    return Boolean(parts)
      && Number.isInteger(parts.year)
      && Number.isInteger(parts.month)
      && Number.isInteger(parts.day)
      && parts.year >= 1900
      && parts.month >= 1
      && parts.month <= 12
      && parts.day >= 1
      && parts.day <= daysInMonth(parts.year, parts.month);
  }

  function pad(value) {
    return String(value).padStart(2, "0");
  }

  function formatDate(parts) {
    return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
  }

  function formatMonth(parts) {
    return `${parts.year}-${pad(parts.month)}`;
  }

  function compareDates(left, right) {
    const a = [left.year, left.month, left.day];
    const b = [right.year, right.month, right.day];
    for (let index = 0; index < a.length; index += 1) {
      if (a[index] !== b[index]) return a[index] < b[index] ? -1 : 1;
    }
    return 0;
  }

  function asOfParts(value) {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return { year: value.getUTCFullYear(), month: value.getUTCMonth() + 1, day: value.getUTCDate() };
    }
    const exact = datePartsFromIso(value || DEFAULT_AS_OF());
    return isRealDate(exact) ? exact : null;
  }

  function addMonths(parts, months) {
    const total = (parts.year * 12) + (parts.month - 1) + Number(months || 0);
    const year = Math.floor(total / 12);
    const month = (total % 12) + 1;
    return { year, month, day: Math.min(parts.day, daysInMonth(year, month)) };
  }

  function completedMonthsBetween(birth, asOf) {
    let months = ((asOf.year - birth.year) * 12) + (asOf.month - birth.month);
    if (asOf.day < birth.day) months -= 1;
    return Math.max(0, months);
  }

  function birthdayInput(input) {
    if (isPlainObject(input)) return input;
    return { birthday: input };
  }

  /**
   * Validate a civil birthday without using local Date parsing. Month-only values
   * deliberately retain no day; a first-of-month calculation anchor is never
   * surfaced as the dog's exact birthday.
   */
  function validateBirthday(input, options) {
    const source = birthdayInput(input);
    const config = isPlainObject(options) ? options : {};
    const asOf = asOfParts(config.asOf || source.ageAsOf || source.asOf);
    if (!asOf) return { valid: false, error: "A valid age-as-of date is required.", birthday: null, birthdayPrecision: "unknown" };

    const rawBirthday = toText(source.birthday || source.birthdate || source.value);
    let precision = canonicalValue(source.birthdayPrecision || source.precision || "");
    if (["exact", "birthday", "date"].includes(precision)) precision = "exact_day";
    if (["approximate", "estimated", "age_estimate", "estimated_age"].includes(precision)) precision = "approximate_age";
    if (!precision) precision = /^\d{4}-\d{2}$/.test(rawBirthday) ? "month_only" : (rawBirthday ? "exact_day" : "unknown");
    if (! ["exact_day", "month_only", "approximate_age", "unknown"].includes(precision)) {
      return { valid: false, error: "Choose an exact birthday or enter an approximate age.", birthday: null, birthdayPrecision: "unknown", ageAsOf: formatDate(asOf) };
    }
    if (precision === "unknown") {
      return {
        valid: true,
        error: null,
        birthday: null,
        birthMonth: null,
        birthdayPrecision: "unknown",
        ageAsOf: formatDate(asOf),
        dayKnown: false,
        isEstimated: true,
      };
    }

    if (precision === "approximate_age") {
      const rawMonths = source.estimatedAgeMonths ?? source.estimated_age_months ?? source.ageMonths ?? source.age_months;
      const rawYears = source.estimatedAgeYears ?? source.estimated_age_years ?? source.ageYears ?? source.age_years;
      // Number("") is zero, but a blank estimate is not an affirmative answer.
      // Preserve an explicit numeric/string zero so a newborn-age answer remains
      // valid while whitespace and missing values stay incomplete.
      const directMonths = toText(rawMonths) ? Number(rawMonths) : NaN;
      const directYears = toText(rawYears) ? Number(rawYears) : NaN;
      const derivedMonths = Number.isFinite(directMonths) && directMonths >= 0
        ? directMonths
        : (Number.isFinite(directYears) && directYears >= 0 ? directYears * 12 : NaN);
      if (!Number.isFinite(derivedMonths) || derivedMonths < 0 || derivedMonths > (30 * 12)) {
        return { valid: false, error: "Enter an approximate age before continuing.", birthday: null, birthdayPrecision: "approximate_age", ageAsOf: formatDate(asOf) };
      }
      const estimatedAgeMonths = Math.round(derivedMonths);
      return {
        valid: true,
        error: null,
        birthday: null,
        birthMonth: null,
        birthdayPrecision: "approximate_age",
        estimatedAgeMonths,
        ageAsOf: formatDate(asOf),
        dayKnown: false,
        isEstimated: true,
      };
    }

    if (precision === "month_only") {
      const parts = monthPartsFromIso(rawBirthday);
      if (!parts || parts.year < 1900 || parts.month < 1 || parts.month > 12) {
        return { valid: false, error: "Enter a real month and year.", birthday: null, birthdayPrecision: "month_only", ageAsOf: formatDate(asOf) };
      }
      const monthStart = { ...parts, day: 1 };
      if (compareDates(monthStart, { year: asOf.year, month: asOf.month, day: 1 }) > 0) {
        return { valid: false, error: "A birthday month cannot be in the future.", birthday: null, birthdayPrecision: "month_only", ageAsOf: formatDate(asOf) };
      }
      return {
        valid: true,
        error: null,
        birthday: null,
        birthMonth: formatMonth(parts),
        birthdayPrecision: "month_only",
        ageAsOf: formatDate(asOf),
        dayKnown: false,
        isEstimated: true,
      };
    }

    const parts = datePartsFromIso(rawBirthday) || birthdayPartsFromSlash(rawBirthday);
    if (!isRealDate(parts)) {
      return { valid: false, error: "Enter a real calendar date.", birthday: null, birthdayPrecision: "exact_day", ageAsOf: formatDate(asOf) };
    }
    if (compareDates(parts, asOf) > 0) {
      return { valid: false, error: "A birthday cannot be in the future.", birthday: null, birthdayPrecision: "exact_day", ageAsOf: formatDate(asOf) };
    }
    return {
      valid: true,
      error: null,
      birthday: formatDate(parts),
      birthMonth: formatMonth(parts),
      birthdayPrecision: "exact_day",
      ageAsOf: formatDate(asOf),
      dayKnown: true,
      isEstimated: false,
    };
  }

  function sizeFromWeight(weight) {
    const value = Number(weight);
    if (!Number.isFinite(value) || value <= 0) return "Unknown";
    if (value < 12) return "Toy";
    if (value < 25) return "Small";
    if (value < 50) return "Medium";
    if (value < 90) return "Large";
    return "Giant";
  }

  function normaliseSize(value) {
    const candidate = toText(value).toLowerCase();
    return ["toy", "small", "medium", "large", "giant"].find((size) => size === candidate)
      ? `${candidate.slice(0, 1).toUpperCase()}${candidate.slice(1)}`
      : "Unknown";
  }

  function stableBreedIdentity(breed) {
    const source = isPlainObject(breed) ? breed : { name: breed };
    return toText(source.breedIdentity || source.breed_identity || source.name);
  }

  function reviewedBreedRecords(value) {
    if (Array.isArray(value)) return value;
    if (isPlainObject(value) && Array.isArray(value.reviewedBreeds)) return value.reviewedBreeds;
    if (isPlainObject(value) && Array.isArray(value.breedCatalog)) return value.breedCatalog;
    if (isPlainObject(value) && Array.isArray(value.breeds)) return value.breeds;
    return [];
  }

  function breedIdentityKey(value) {
    return toText(value).toLowerCase().replace(/[‐‑‒–—]/g, "-").replace(/\s+/g, " ");
  }

  function taxonomyGroups(taxonomy) {
    if (!taxonomy) return REVIEWED_BREED_GROUPS;
    if (isPlainObject(taxonomy.groupByQuizBreedName)) return taxonomy.groupByQuizBreedName;
    if (isPlainObject(taxonomy.breedGroups)) return taxonomy.breedGroups;
    if (isPlainObject(taxonomy.groups)) return taxonomy.groups;
    if (isPlainObject(taxonomy)) return taxonomy;
    return REVIEWED_BREED_GROUPS;
  }

  function invalidBreedProfile(identity, reason) {
    return {
      valid: false,
      reviewed: false,
      reviewReason: reason,
      breedIdentity: identity || null,
      name: identity || "Unreviewed breed",
      size: "Unknown",
      sizeBasis: "unreviewed_fail_closed",
      minAge: SIZE_PROFILES.Unknown.minAge,
      seniorAge: SIZE_PROFILES.Unknown.seniorAge,
      flags: [],
      group: null,
      groupReviewed: false,
      workingOrSporting: false,
      mixedOrUnknown: false,
    };
  }

  /**
   * Rehydrates a stable persisted breed identity against the current reviewed
   * rd-fit-breeds.json input. Persisted size, ages, flags, group and weight text
   * are ignored. Generic Mixed Breed and Unknown are reviewed special values
   * whose size thresholds are derived from current weight on every call.
   */
  function rehydrateBreedSelection(storedBreed, currentReviewedBreeds, currentWeight, taxonomy) {
    const identity = stableBreedIdentity(storedBreed);
    if (!identity) return invalidBreedProfile(null, "missing_breed_identity");
    const identityKey = breedIdentityKey(identity);
    if (["mixed breed", "unknown"].includes(identityKey)) {
      const size = sizeFromWeight(currentWeight);
      const profileSize = SIZE_PROFILES[size] ? size : "Unknown";
      const ages = SIZE_PROFILES[profileSize];
      const name = identityKey === "mixed breed" ? "Mixed Breed" : "Unknown";
      return {
        valid: true,
        reviewed: true,
        reviewReason: null,
        breedIdentity: name,
        name,
        size: profileSize,
        sizeBasis: profileSize === "Unknown" ? "conservative_unknown" : "weight_threshold",
        minAge: ages.minAge,
        seniorAge: ages.seniorAge,
        flags: [],
        group: null,
        groupReviewed: false,
        workingOrSporting: false,
        mixedOrUnknown: true,
      };
    }

    const records = reviewedBreedRecords(currentReviewedBreeds);
    if (!records.length) return invalidBreedProfile(identity, "reviewed_breed_catalog_unavailable");
    const requestedName = BREED_IDENTITY_ALIASES[identityKey] || identity;
    const requestedKey = breedIdentityKey(requestedName);
    const matches = records.filter((record) => isPlainObject(record) && breedIdentityKey(record.name) === requestedKey);
    if (matches.length !== 1) {
      return invalidBreedProfile(identity, matches.length ? "ambiguous_reviewed_breed_identity" : "unreviewed_breed_identity");
    }
    const record = matches[0];
    const name = toText(record.name);
    const size = normaliseSize(record.size);
    const minAge = Number(record.minAge);
    const seniorAge = Number(record.seniorAge);
    if (!name || size === "Unknown" || !Number.isFinite(minAge) || minAge <= 0 || !Number.isFinite(seniorAge) || seniorAge <= 0) {
      return invalidBreedProfile(identity, "invalid_reviewed_breed_record");
    }
    const flags = unique(asArray(record.flags).map(canonicalValue).filter(Boolean));
    const mixedOrUnknown = /^mixed\s*breed\s*[—-]/i.test(name);
    const groups = taxonomyGroups(taxonomy);
    const group = mixedOrUnknown ? null : (groups[name] || null);
    return {
      valid: true,
      reviewed: true,
      reviewReason: null,
      breedIdentity: name,
      name,
      size,
      sizeBasis: mixedOrUnknown ? "reviewed_declared_mixed_size" : "reviewed_breed_record",
      minAge,
      seniorAge,
      flags,
      group: ["Working", "Sporting"].includes(group) ? group : null,
      groupReviewed: ["Working", "Sporting"].includes(group),
      workingOrSporting: ["Working", "Sporting"].includes(group),
      mixedOrUnknown,
    };
  }

  function deriveBreedProfile(breed, currentWeight, taxonomy, currentReviewedBreeds) {
    return rehydrateBreedSelection(breed, currentReviewedBreeds, currentWeight, taxonomy);
  }

  /**
   * Derive age in civil calendar months. For a month-only birthday, lower and
   * upper bounds are retained; the lower bound controls puppy eligibility and
   * the upper bound triggers a conservative senior path at the boundary.
   */
  function deriveAge(stateOrBirthday, breedProfile, options) {
    const profile = breedProfile || SIZE_PROFILES.Unknown;
    const source = birthdayInput(stateOrBirthday);
    const config = isPlainObject(options) ? options : {};
    const birthday = source.valid === true && source.birthdayPrecision ? source : validateBirthday(source, { asOf: config.asOf || source.ageAsOf });
    const asOf = asOfParts(birthday.ageAsOf || config.asOf || source.ageAsOf);
    if (!birthday.valid || !asOf) {
      return { valid: false, error: birthday.error || "A valid birthday is required.", ageMonths: null, ageYears: null, ageMonthsRange: null, ageAsOf: birthday.ageAsOf || null, nextEligibleDate: null, nextEligibleDatePrecision: "unknown", isEstimated: true, isPuppy: true, isSenior: false };
    }
    if (birthday.birthdayPrecision === "unknown") {
      return { valid: true, error: null, ageMonths: null, ageYears: null, ageMonthsRange: null, ageAsOf: birthday.ageAsOf, nextEligibleDate: null, nextEligibleDatePrecision: "unknown", isEstimated: true, isPuppy: true, isSenior: false, eligibilityUnknown: true };
    }
    const minAge = Number(profile.minAge || SIZE_PROFILES.Unknown.minAge);
    const seniorAge = Number(profile.seniorAge || SIZE_PROFILES.Unknown.seniorAge);
    if (birthday.birthdayPrecision === "approximate_age") {
      const ageMonths = Number(birthday.estimatedAgeMonths);
      if (!Number.isFinite(ageMonths) || ageMonths < 0) {
        return { valid: false, error: "A valid approximate age is required.", ageMonths: null, ageYears: null, ageMonthsRange: null, ageAsOf: birthday.ageAsOf, nextEligibleDate: null, nextEligibleDatePrecision: "unknown", isEstimated: true, isPuppy: true, isSenior: false };
      }
      const remainingMonths = Math.max(0, minAge - ageMonths);
      const estimatedEligible = remainingMonths ? addMonths(asOf, remainingMonths) : null;
      return {
        valid: true,
        error: null,
        ageMonths,
        ageYears: Number((ageMonths / 12).toFixed(3)),
        ageMonthsRange: { min: ageMonths, max: ageMonths },
        ageAsOf: birthday.ageAsOf,
        nextEligibleDate: estimatedEligible ? formatMonth(estimatedEligible) : null,
        nextEligibleDatePrecision: estimatedEligible ? "month_only" : "unknown",
        isEstimated: true,
        isPuppy: ageMonths < minAge,
        isSenior: ageMonths >= seniorAge * 12,
      };
    }
    if (birthday.birthdayPrecision === "month_only") {
      const month = monthPartsFromIso(birthday.birthMonth);
      const earliest = { ...month, day: 1 };
      const latest = { ...month, day: daysInMonth(month.year, month.month) };
      const minMonths = completedMonthsBetween(latest, asOf);
      const maxMonths = completedMonthsBetween(earliest, asOf);
      const isPuppy = minMonths < minAge;
      const isSenior = maxMonths >= seniorAge * 12;
      const latestEligible = addMonths(latest, minAge);
      return {
        valid: true,
        error: null,
        ageMonths: minMonths,
        ageYears: Number((minMonths / 12).toFixed(3)),
        ageMonthsRange: { min: minMonths, max: maxMonths },
        ageAsOf: birthday.ageAsOf,
        nextEligibleDate: isPuppy ? formatMonth(latestEligible) : null,
        nextEligibleDatePrecision: isPuppy ? "month_only" : "unknown",
        isEstimated: true,
        isPuppy,
        isSenior,
      };
    }
    const exact = datePartsFromIso(birthday.birthday);
    const ageMonths = completedMonthsBetween(exact, asOf);
    const isPuppy = ageMonths < minAge;
    return {
      valid: true,
      error: null,
      ageMonths,
      ageYears: Number((ageMonths / 12).toFixed(3)),
      ageMonthsRange: { min: ageMonths, max: ageMonths },
      ageAsOf: birthday.ageAsOf,
      nextEligibleDate: isPuppy ? formatDate(addMonths(exact, minAge)) : null,
      nextEligibleDatePrecision: isPuppy ? "exact_day" : "unknown",
      isEstimated: false,
      isPuppy,
      isSenior: ageMonths >= seniorAge * 12,
    };
  }

  function formatDogName(value) {
    return toText(value) || "your dog";
  }

  function formatBreedToken(profile) {
    if (!profile || profile.mixedOrUnknown) {
      return profile && /unknown/i.test(profile.name || "") ? "dogs with an unknown breed" : "mixed-breed dogs";
    }
    return toText(profile.name) || "your dog";
  }

  function interpolateInterstitialCopy(copy, state, profile) {
    return String(copy || "")
      .replace(/\[dog_name\]/g, formatDogName(state && state.name))
      .replace(/\[Breed\]/g, formatBreedToken(profile));
  }

  function configuredCopyIsSafe(value) {
    const copy = toText(value);
    return Boolean(copy) && !BLOCKED_COPY_PATTERNS.some((pattern) => pattern.test(copy));
  }

  function interstitialForId(id) {
    return INTERSTITIAL_BY_ID[id] || null;
  }

  /**
   * Deterministic compatibility priority used by the three V3 chapter cards:
   * step 2: copper > urate > fat > Giant > Toy/Small > default;
   * step 3: puppy > senior > default;
   * step 9: coat > digestion > interest > default.
   * This makes multi-flag / multi-select outcomes reproducible without using
   * the blocked Spec Copy text.
   */
  function selectInterstitial(step, state, breedProfile, overrides) {
    const source = state || {};
    const profile = breedProfile || deriveBreedProfile(source.breed, source.currentWeight);
    const age = deriveAge(source, profile, { asOf: source.ageAsOf });
    const health = normaliseSelection(source.health, "none").filter((value) => value !== "none");
    const proteins = normaliseSelection(source.proteins, "none").filter((value) => value !== "none");
    const outcomes = normaliseSelection(source.outcomes, "none").filter((value) => value !== "none");
    const numericStep = Number(step);
    let id = "RT-01-D";
    if (numericStep === 2) {
      if (profile.flags.includes("copper")) id = "RT-02-COPPER";
      else if (profile.flags.includes("urate")) id = "RT-02-URATE";
      else if (profile.flags.includes("fat")) id = "RT-02-FAT";
      else if (profile.size === "Giant") id = "RT-02-GIANT";
      else if (["Toy", "Small"].includes(profile.size)) id = "RT-02-SMALL";
      else id = "RT-02-D";
    } else if (numericStep === 3) {
      id = age.isPuppy ? "RT-03-PUPPY" : (age.isSenior ? "RT-03-SENIOR" : "RT-03-D");
    } else if (numericStep === 4) {
      id = source.goal === "lose" ? "RT-04-LOSE" : (source.goal === "gain" ? "RT-04-GAIN" : "RT-04-D");
    } else if (numericStep === 5) {
      id = source.activity === "high" ? "RT-05-HIGH" : (source.activity === "low" ? "RT-05-LOW" : "RT-05-D");
    } else if (numericStep === 6) {
      id = source.rawStatus === "sometimes" ? "RT-06-SOME" : (source.rawStatus === "not_yet" ? "RT-06-NEW" : "RT-06-REG");
    } else if (numericStep === 7) {
      id = proteins.length ? "RT-07-AVOID" : "RT-07-D";
    } else if (numericStep === 8) {
      id = health.length ? "RT-08-FLAG" : "RT-08-D";
    } else if (numericStep === 9) {
      id = outcomes.includes("coat") ? "RT-09-COAT" : (outcomes.includes("digestion") ? "RT-09-DIGEST" : (outcomes.includes("interest") ? "RT-09-INTEREST" : "RT-09-D"));
    }
    const selected = interstitialForId(id) || interstitialForId("RT-01-D");
    const configured = isPlainObject(overrides) ? overrides[selected.id] : null;
    const overrideCopy = typeof configured === "string" ? configured : (configured && configured.copy);
    const copy = configuredCopyIsSafe(overrideCopy) ? toText(overrideCopy) : selected.copy;
    return {
      id: selected.id,
      step: selected.step,
      type: selected.type,
      condition: selected.condition,
      sourceStatus: selected.status,
      blockedClaimDisabled: ["Blocked", "Evidence gate", "Remove"].includes(selected.status),
      copy,
      renderedCopy: interpolateInterstitialCopy(copy, source, profile),
      tokens: { dog_name: formatDogName(source.name), Breed: formatBreedToken(profile) },
    };
  }

  function selectChapterCard(questionNumber, state) {
    const card = CHAPTER_CARD_BY_QUESTION[Number(questionNumber)] || null;
    if (!card) return null;
    const source = state || {};
    return {
      ...card,
      renderedCopy: interpolateInterstitialCopy(card.copy, source, null),
      tokens: { dog_name: formatDogName(source.name) },
    };
  }

  function createDefaultState() {
    return {
      schemaVersion: SCHEMA_VERSION,
      calculationRevision: CALCULATION_REVISION,
      bodyConditionReconfirmationPending: false,
      currentView: "intro",
      view: "intro",
      step: 0,
      name: "",
      breed: null,
      birthday: "",
      // Exact birthday is the selected/default mode. An approximate age is
      // available, but must carry an actual estimate instead of a fabricated
      // birthday.
      birthdayPrecision: "exact_day",
      estimatedAgeMonths: null,
      ageAsOf: null,
      nextEligibleDate: null,
      nextEligibleDatePrecision: "unknown",
      currentWeight: "",
      bodyCondition: "",
      goal: "maintain",
      targetWeight: "",
      activity: "moderate",
      rawStatus: "not_yet",
      proteins: [],
      toleratedProtein: "no_preference",
      health: [],
      outcomes: [],
      primaryOutcome: "",
      mealsPerDay: 2,
      purchaseType: "subscription",
      cadence: "4",
      acknowledged: false,
      dogId: null,
      dog_id: null,
      completionId: null,
      completion_id: null,
      result: null,
      planEmailStatus: "idle",
    };
  }

  /**
   * Never translate V1 ageYears/ageMonths into a fabricated birthday. A V1
   * in-progress/result state resumes at the V3 birthday step, clears derived
   * lifecycle data and result/completion state, and preserves the other answers.
   */
  function migrateState(input, options) {
    const legacy = isPlainObject(input) ? input : {};
    const defaults = createDefaultState();
    const isCurrent = legacy.schemaVersion === SCHEMA_VERSION || legacy.schema_version === SCHEMA_VERSION;
    const next = { ...defaults, ...legacy };
    next.schemaVersion = SCHEMA_VERSION;
    next.schema_version = SCHEMA_VERSION;
    next.calculationRevision = CALCULATION_REVISION;
    next.currentView = legacy.currentView || legacy.view || defaults.currentView;
    next.view = next.currentView;
    const breedIdentity = stableBreedIdentity(legacy.breed || legacy.breedName || legacy.breed_name);
    next.breed = breedIdentity ? { name: breedIdentity } : null;
    next.proteins = normaliseSelection(legacy.proteins, "none");
    const bodyCondition = canonicalValue(legacy.bodyCondition || legacy.body_condition);
    // Only an explicit current-revision answer is authoritative. Earlier
    // sessions defaulted to ideal and must reconfirm instead of inheriting it.
    next.bodyCondition = ["underweight", "ideal", "overweight"].includes(bodyCondition)
      ? bodyCondition
      : "";
    next.goal = ({ underweight: "gain", ideal: "maintain", overweight: "lose" })[next.bodyCondition] || "";
    next.targetWeight = "";
    const submittedHealth = normaliseSelection(legacy.health, "none");
    // An empty current-schema safety answer is still unanswered. Preserve it
    // across reload instead of allowing the classifier's ready-state fallback
    // to fabricate an explicit "None" selection and bypass the Q8 gate.
    next.health = submittedHealth.length ? normaliseConditionSelection(submittedHealth) : [];
    next.outcomes = normaliseSelection(legacy.outcomes, "none");
    next.toleratedProtein = normaliseToleratedProtein(legacy.toleratedProtein || legacy.tolerated_protein || legacy.knownToleratedProtein || legacy.known_tolerated_protein);
    next.mealsPerDay = normaliseMealsPerDay(legacy.mealsPerDay || legacy.meals_per_day);
    next.purchaseType = normalisePurchaseType(legacy.purchaseType || legacy.purchase_type);
    const dogId = toText(legacy.dogId || legacy.dog_id) || null;
    const completionId = toText(legacy.completionId || legacy.completion_id) || null;
    next.dogId = dogId;
    next.dog_id = dogId;
    next.completionId = completionId;
    next.completion_id = completionId;
    const ageAsOf = toText(legacy.ageAsOf || legacy.age_as_of) || null;
    next.ageAsOf = ageAsOf;
    next.age_as_of = ageAsOf;
    if (!isCurrent) {
      next.bodyCondition = "";
      next.body_condition = "";
      next.goal = "";
      next.bodyConditionReconfirmationPending = true;
      delete next.returnToEmailAfterEdit;
      delete next.returnToResultAfterEdit;
      delete next.emailEditStep;
      delete next.resultEditStep;
      next.birthday = "";
      next.birthdayPrecision = "exact_day";
      next.estimatedAgeMonths = null;
      next.ageAsOf = null;
      next.age_as_of = null;
      next.nextEligibleDate = null;
      next.next_eligible_date = null;
      next.nextEligibleDatePrecision = "unknown";
      next.result = null;
      next.completionId = null;
      next.completion_id = null;
      next.completionSnapshotAt = null;
      next.completion_snapshot_at = null;
      next.eventAcknowledgements = null;
      next.planRequestAcknowledged = false;
      next.currentView = "step";
      next.view = "step";
      next.step = 3;
      next.migration = {
        fromSchemaVersion: legacy.schemaVersion || "rd-fit-quiz-v1",
        policy: "body_condition_reconfirmation_no_synthesized_target",
      };
      return next;
    }
    const validation = validateBirthday(next, { asOf: (isPlainObject(options) && options.asOf) || next.ageAsOf || DEFAULT_AS_OF() });
    if (validation.valid) {
      next.birthday = validation.birthday || validation.birthMonth || "";
      next.birthdayPrecision = validation.birthdayPrecision;
      next.estimatedAgeMonths = validation.estimatedAgeMonths ?? null;
      next.ageAsOf = validation.ageAsOf;
      next.age_as_of = validation.ageAsOf;
    }
    const calculationNeedsReconfirmation = legacy.bodyConditionReconfirmationPending === true
      || (!hasOwn(legacy, "bodyCondition") && !hasOwn(legacy, "body_condition"))
      || toText(legacy.calculationRevision) !== CALCULATION_REVISION;
    if (calculationNeedsReconfirmation) {
      // Stamping the current revision is not confirmation. This marker must
      // survive reloads until the weight/body-condition question is validated.
      next.bodyConditionReconfirmationPending = true;
      next.bodyCondition = "";
      next.body_condition = "";
      next.goal = "";
      delete next.returnToEmailAfterEdit;
      delete next.returnToResultAfterEdit;
      delete next.emailEditStep;
      delete next.resultEditStep;
      next.result = null;
      next.completionId = null;
      next.completion_id = null;
      next.completionSnapshotAt = null;
      next.completion_snapshot_at = null;
      next.eventAcknowledgements = null;
      next.planRequestAcknowledged = false;
      // Interstitial/chapter stores the question just completed; the UI would
      // otherwise advance past weight when restoring a saved step-3 chapter.
      const pastBodyCondition = (["question", "step"].includes(next.currentView) && Number(next.step) > 3)
        || (["interstitial", "chapter", "v34_education"].includes(next.currentView) && Number(next.step) >= 3);
      if (["email", "result"].includes(next.currentView) || pastBodyCondition) {
        next.currentView = "step";
        next.view = "step";
        next.step = 3;
      }
      next.migration = {
        fromCalculationRevision: toText(legacy.calculationRevision) || "pre-body-condition",
        policy: "body_condition_reconfirmation_no_synthesized_target",
      };
    } else {
      const currentResult = isPlainObject(legacy.result)
        && toText(legacy.result.calculationRevision) === CALCULATION_REVISION;
      next.result = currentResult ? clone(legacy.result) : null;
      // The result screen recalculates before rendering. It is the safe resume
      // route when an email session has no current cached calculation, rather
      // than allowing the email sender to serialize an empty result object.
      if (!currentResult && next.currentView === "email") {
        next.currentView = "result";
        next.view = "result";
      }
    }
    return next;
  }

  function normaliseProtein(value) {
    const candidate = canonicalValue(value).replace(/_/g, " ");
    return KNOWN_PROTEINS.includes(candidate) ? candidate : null;
  }

  function normaliseToleratedProtein(value) {
    const values = unique(asArray(value)
      .map((item) => canonicalValue(item).replace(/_/g, " "))
      .filter(Boolean));
    const proteins = values.map(normaliseProtein).filter(Boolean);
    // The UI captures one known tolerated protein. Accepting an old/malformed
    // multi-value state lets the recommendation engine expose only a genuine
    // exact tie instead of silently discarding a concrete answer.
    if (proteins.length) return unique(proteins);
    return "no_preference";
  }

  function toleratedProteins(value) {
    const normalised = normaliseToleratedProtein(value);
    return Array.isArray(normalised) ? normalised : [];
  }

  function normaliseCondition(value) {
    const key = canonicalValue(value);
    return CONDITION_ALIASES[key] || key;
  }

  function normaliseConditionSelection(values) {
    const selected = normaliseSelection(values, "none");
    const concrete = unique(selected
      .filter((value) => value !== "none")
      .map(normaliseCondition)
      .filter(Boolean));
    return concrete.length ? concrete : ["none"];
  }

  function normaliseMealsPerDay(value) {
    const meals = Number(value);
    return [1, 2, 3].includes(meals) ? meals : 2;
  }

  function normalisePurchaseType(value) {
    const type = canonicalValue(value);
    return ["one_time", "one-time", "onetime"].includes(type) ? "one_time" : "subscription";
  }

  function normaliseAvoidances(value) {
    return normaliseSelection(value, "none")
      .filter((item) => item !== "none")
      .map(normaliseProtein)
      .filter(Boolean);
  }

  function avoidanceSelection(value) {
    const selected = normaliseSelection(value, "none").filter((item) => item !== "none");
    const unknown = selected.filter((item) => !normaliseProtein(item));
    return { avoidances: unique(selected.map(normaliseProtein).filter(Boolean)), unknown };
  }

  function sameFormulaRecord(left, right) {
    if (!isPlainObject(left) || !isPlainObject(right)) return false;
    return left.role === right.role
      && left.handle === right.handle
      && left.title === right.title
      && left.ingredients === right.ingredients;
  }

  /* A page may explicitly mark a formula unavailable by passing null when its
   * configured product handle does not match. Any non-null catalog entry must
   * be byte-for-byte the reviewed ingredient record above; otherwise a result
   * fails closed instead of trusting mutable product copy. */
  function resolveFormulaCatalog(value) {
    if (value === undefined) return { valid: true, formulas: FORMULA_CATALOG, source: "reviewed_default" };
    if (!isPlainObject(value)) return { valid: false, formulas: {}, source: "unverified" };
    const formulas = {};
    for (const role of FORMULA_ROLES) {
      if (!hasOwn(value, role)) return { valid: false, formulas: {}, source: "unverified" };
      if (value[role] === null) {
        formulas[role] = null;
      } else if (sameFormulaRecord(value[role], FORMULA_CATALOG[role])) {
        formulas[role] = FORMULA_CATALOG[role];
      } else {
        return { valid: false, formulas: {}, source: "unverified" };
      }
    }
    if (Object.keys(value).some((key) => !FORMULA_ROLES.includes(key))) return { valid: false, formulas: {}, source: "unverified" };
    return { valid: true, formulas, source: "reviewed_supplied" };
  }

  function formulaContainsProtein(formula, protein) {
    if (!formula) return false;
    return new RegExp(`\\b${protein.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\$&")}\\b`, "i").test(formula.ingredients);
  }

  function roleIsAllowed(role, avoidances, formulas) {
    const required = ROLE_PROTEINS[role];
    const catalog = formulas || FORMULA_CATALOG;
    if (role === "variety") return Boolean(required);
    const formula = catalog[role];
    const avoid = new Set(avoidances);
    return Boolean(required && formula) && ![...avoid].some((protein) => formulaContainsProtein(formula, protein));
  }

  function normaliseBoxSizes(value) {
    return unique(asArray(value === undefined ? BOX_SIZES : value)
      .map(Number)
      .filter((size) => BOX_SIZES.includes(size)))
      .sort((left, right) => left - right);
  }

  function configuredVarietyVariants(config) {
    if (Array.isArray(config.varietyVariants)) return config.varietyVariants;
    if (config.variantsByRole && Array.isArray(config.variantsByRole.variety)) return config.variantsByRole.variety;
    return null;
  }

  /*
   * Determines which box sizes still have a compatible actual Variety mix.
   * With a loaded inventory it verifies that inventory. Before it is loaded it
   * uses the reviewed current option contract above, which deliberately makes
   * a salmon/beef avoidance 8/16-only and makes a turkey avoidance impossible.
   */
  function varietyEligibility(avoidances, config, formulas) {
    const excluded = new Set(avoidances);
    const requestedSizes = normaliseBoxSizes(config.availableBoxSizes);
    const actualVariants = configuredVarietyVariants(config);
    const bySize = {};
    for (const size of requestedSizes) {
      if (actualVariants) {
        const selected = selectRecommendedSubscriptionVariant("variety", size, actualVariants, avoidances);
        if (selected.valid && selected.proteinComposition.every((protein) => roleIsAllowed(protein === "salmon" ? "salmon" : protein, avoidances, formulas))) {
          bySize[size] = {
            label: selected.variant.options && selected.variant.options.find((value) => !/^\d+\s*lb$/i.test(toText(value))) || selected.variant.title,
            proteins: selected.proteinComposition,
            source: selected.compositionSource,
          };
        }
        continue;
      }
      const label = (VARIETY_OPTION_LABELS_BY_SIZE[size] || []).find((candidate) => {
        const composition = CANONICAL_VARIANT_COMPOSITIONS[candidate] || [];
        return composition.length >= 2 && composition.every((protein) => (
          !excluded.has(protein) && roleIsAllowed(protein === "salmon" ? "salmon" : protein, avoidances, formulas)
        ));
      });
      if (label) bySize[size] = {
        label,
        proteins: [...CANONICAL_VARIANT_COMPOSITIONS[label]],
        source: "reviewed_current_option_contract",
      };
    }
    return { sizes: Object.keys(bySize).map(Number).sort((left, right) => left - right), bySize };
  }

  function hasSuppliedVariantCatalog(config) {
    return isPlainObject(config && config.variantsByRole);
  }

  function normaliseCadenceWeeks(value) {
    const cadenceWeeks = Number(value);
    return SUPPORTED_SUBSCRIPTION_CADENCE_WEEKS.includes(cadenceWeeks) ? cadenceWeeks : null;
  }

  function planCadenceWeeks(plan) {
    if (!isPlainObject(plan) || !toText(plan.id)) return null;
    const interval = canonicalValue(plan.interval || plan.intervalUnit || plan.deliveryInterval || "");
    const count = Number(plan.intervalCount ?? plan.interval_count ?? plan.frequency ?? plan.every);
    if (Number.isInteger(count) && count > 0 && ["week", "weeks", "weekly"].includes(interval)) return count;
    const name = normaliseVariantLabel(plan.name || plan.title || "");
    const match = name.match(/(?:^|[^\d])(?:(?:every|deliver(?:y)?\s+every)\s+)?(\d+)\s*(?:weeks?|wks?)(?=$|[^\d])/i);
    return match ? Number(match[1]) : null;
  }

  function planIsEveryCadenceWeeks(plan, cadenceWeeks) {
    const expectedCadence = normaliseCadenceWeeks(cadenceWeeks);
    return Boolean(expectedCadence) && planCadenceWeeks(plan) === expectedCadence;
  }

  function planIsEveryFourWeeks(plan) {
    return planIsEveryCadenceWeeks(plan, 4);
  }

  function plansForCadenceForVariant(variant, cadenceWeeks) {
    if (!isPlainObject(variant) || !normaliseCadenceWeeks(cadenceWeeks)) return [];
    return asArray(variant.sellingPlans || variant.selling_plans)
      .filter((plan) => planIsEveryCadenceWeeks(plan, cadenceWeeks))
      .map((plan) => ({ ...plan, id: String(plan.id) }));
  }

  function fourWeekPlansForVariant(variant) {
    return plansForCadenceForVariant(variant, 4);
  }

  function selectRecommendedSubscriptionVariant(role, size, variants, avoidances, cadenceWeeks) {
    const cadence = cadenceWeeks === undefined ? 4 : normaliseCadenceWeeks(cadenceWeeks);
    const candidates = asArray(variants).map((variant) => {
      const selected = selectRecommendedVariant(role, size, [variant], avoidances);
      const allPlans = selected.valid
        ? asArray(variant.sellingPlans || variant.selling_plans)
          .filter((plan) => isPlainObject(plan) && toText(plan.id))
          .map((plan) => ({ ...plan, id: String(plan.id) }))
        : [];
      const plans = allPlans
        .filter((plan) => planIsEveryCadenceWeeks(plan, cadence))
        .sort((left, right) => String(left.id).localeCompare(String(right.id)));
      return { selected, plans, allPlans };
    }).filter((candidate) => candidate.selected.valid && candidate.plans.length)
      .sort((left, right) => {
        if (role !== "variety") return 0;
        return right.selected.proteinComposition.length - left.selected.proteinComposition.length;
      });
    if (!candidates.length) {
      return { valid: false, variant: null, proteinComposition: [], sellingPlans: [], reason: "No available variant of this size has the selected subscription cadence." };
    }
    return {
      ...candidates[0].selected,
      // The result still verifies its four-week default independently, while
      // the UI must retain the full allocation list to validate a deliberate
      // two-week choice against the exact selected variant later.
      sellingPlans: candidates[0].allPlans,
      fourWeekSellingPlans: candidates[0].allPlans.filter(planIsEveryFourWeeks),
      sellingPlan: candidates[0].plans[0],
    };
  }

  function commonCadencePlan(selections, cadenceWeeks) {
    const cadence = normaliseCadenceWeeks(cadenceWeeks);
    if (!cadence) return null;
    const validSelections = asArray(selections).filter((selection) => selection && selection.variant);
    if (!validSelections.length) return null;
    const resolved = validSelections.map((selection) => {
      const plans = asArray(selection.sellingPlans || plansForCadenceForVariant(selection.variant, cadence))
        .filter((plan) => planIsEveryCadenceWeeks(plan, cadence))
        .sort((left, right) => String(left.id).localeCompare(String(right.id)));
      const selectedPlan = planIsEveryCadenceWeeks(selection.sellingPlan, cadence)
        ? selection.sellingPlan
        : null;
      return { selection, plan: selectedPlan || plans[0] || null };
    });
    if (resolved.some((item) => !item.plan || !toText(item.plan.id))) return null;
    const ids = unique(resolved.map((item) => String(item.plan.id))).sort();
    const linePlanIds = resolved.reduce((all, item) => {
      all[String(item.selection.size)] = String(item.plan.id);
      return all;
    }, {});
    return {
      id: ids.length === 1 ? ids[0] : null,
      name: `Every ${cadence} Weeks`,
      cadenceWeeks: cadence,
      linePlanIds,
    };
  }

  function commonFourWeekPlan(selections) {
    return commonCadencePlan(selections, 4);
  }

  function verifiedPlanPricing(selections, sellingPlan, currency) {
    if (!sellingPlan) return null;
    let oneTimePriceCents = 0;
    let subscriptionPriceCents = 0;
    for (const selection of asArray(selections)) {
      const quantity = Number(selection && selection.quantity);
      const variantPrice = Number(selection && selection.variant && selection.variant.price);
      const planId = toText(sellingPlan.linePlanIds && sellingPlan.linePlanIds[String(selection.size)])
        || toText(sellingPlan.id);
      const allocation = asArray(selection && selection.sellingPlans)
        .find((plan) => String(plan && plan.id) === planId);
      const subscriptionPrice = Number(allocation && allocation.price);
      if (!Number.isInteger(quantity) || quantity <= 0 || !Number.isFinite(variantPrice) || variantPrice < 0 || !Number.isFinite(subscriptionPrice) || subscriptionPrice < 0) {
        return null;
      }
      oneTimePriceCents += variantPrice * quantity;
      subscriptionPriceCents += subscriptionPrice * quantity;
    }
    const savingsCents = Math.max(0, oneTimePriceCents - subscriptionPriceCents);
    return {
      currency: toText(currency || "USD").toUpperCase(),
      oneTimePriceCents,
      subscriptionPriceCents,
      savingsCents,
      savingsPercent: oneTimePriceCents > 0
        ? Number(((savingsCents / oneTimePriceCents) * 100).toFixed(1))
        : 0,
    };
  }

  function verifiedRoleInventory(role, avoidances, config) {
    const variants = hasSuppliedVariantCatalog(config) ? config.variantsByRole[role] : null;
    const bySize = {};
    if (!Array.isArray(variants)) return { sizes: [], bySize, supplied: hasSuppliedVariantCatalog(config) };
    for (const size of normaliseBoxSizes(config.availableBoxSizes)) {
        const selected = selectRecommendedSubscriptionVariant(role, size, variants, avoidances);
      if (!selected.valid) continue;
      bySize[size] = {
        label: selected.compositionLabel || selected.variant.options?.find((value) => !/^\d+\s*lb$/i.test(toText(value))) || selected.variant.title,
        proteins: selected.proteinComposition,
        source: selected.compositionSource,
        variantId: selected.variant.id,
      };
    }
    return { sizes: Object.keys(bySize).map(Number).sort((left, right) => left - right), bySize, supplied: true };
  }

  function verifyCadenceBoxPlanInventory(role, boxPlan, avoidances, config, cadenceWeeks) {
    const cadence = normaliseCadenceWeeks(cadenceWeeks);
    if (!cadence) {
      return {
        valid: false,
        selections: [],
        sellingPlan: null,
        pricing: null,
        reason: "The requested subscription cadence is not supported for cart verification.",
      };
    }
    if (!hasSuppliedVariantCatalog(config)) return { valid: true, selections: [] };
    const variants = config.variantsByRole[role];
    if (!Array.isArray(variants) || !isPlainObject(boxPlan && boxPlan.quantities)) {
      return { valid: false, selections: [] };
    }
    const selections = [];
    for (const [rawSize, rawQuantity] of Object.entries(boxPlan.quantities)) {
      const size = Number(rawSize);
      const quantity = Number(rawQuantity);
      if (!BOX_SIZES.includes(size) || !Number.isInteger(quantity) || quantity < 0) return { valid: false, selections: [] };
      if (!quantity) continue;
      const selected = selectRecommendedSubscriptionVariant(role, size, variants, avoidances, cadence);
      if (!selected.valid) return { valid: false, selections: [] };
      selections.push({
        size,
        quantity,
        variant: selected.variant,
        proteinComposition: selected.proteinComposition,
        sellingPlans: selected.sellingPlans,
        sellingPlan: selected.sellingPlan,
      });
    }
    const sellingPlan = commonCadencePlan(selections, cadence);
    const pricing = verifiedPlanPricing(selections, sellingPlan, config.currency);
    const valid = selections.length > 0 && Boolean(sellingPlan) && Boolean(pricing);
    return {
      valid,
      selections,
      sellingPlan,
      pricing,
      reason: !sellingPlan
        ? `No common Every ${cadence} Weeks selling plan is allocated to every required box.`
        : (!pricing ? "The current one-time and subscription prices could not be verified for every required box." : null),
    };
  }

  // Four weeks remains the baseline availability gate for all recommendations.
  // Other cadences are independently re-verified only when a customer selects
  // one, so a two-week plan can never silently substitute for the default.
  function verifyBoxPlanInventory(role, boxPlan, avoidances, config) {
    return verifyCadenceBoxPlanInventory(role, boxPlan, avoidances, config, 4);
  }

  function healthClassification(health) {
    const normalized = normaliseConditionSelection(health);
    const answered = normaliseSelection(health, "none").length > 0;
    const selected = normalized.filter((value) => value !== "none");
    const waitFlags = selected.filter((value) => WAIT_CONDITION_FLAGS.has(value));
    const conditionalFlags = selected.filter((value) => CONDITIONAL_CONDITION_FLAGS.has(value));
    const unclassifiedFlags = selected.filter((value) => !WAIT_CONDITION_FLAGS.has(value) && !CONDITIONAL_CONDITION_FLAGS.has(value));
    // Never downgrade an unrecognized persisted condition to ready. It is a
    // conditional route until a reviewed UI/data mapping is supplied.
    const allConditionalFlags = [...conditionalFlags, ...unclassifiedFlags];
    const status = waitFlags.length ? "wait" : (allConditionalFlags.length ? "conditional" : "ready");
    return {
      selected,
      answered,
      waitFlags,
      conditionalFlags: allConditionalFlags,
      watchFlags: allConditionalFlags,
      level: status,
      status,
    };
  }

  function breedWatchFlags(profile) {
    return WATCH_FLAG_PRIORITY.filter((flag) => profile.flags.includes(flag));
  }

  function resolveBreedRules(profile) {
    const empty = { flags: [], excludedProteins: [], preferredRoles: [], notices: [] };
    if (!profile || profile.valid !== true || profile.reviewed !== true || profile.mixedOrUnknown) return empty;
    const name = toText(profile.name);
    const fatRestricted = FAT_RESTRICTED_BREEDS.has(name);
    const flags = WATCH_FLAG_PRIORITY.filter((flag) => flag === "fat"
      ? fatRestricted
      : asArray(profile.flags).includes(flag));
    const messages = {
      fat: name === "Miniature Schnauzer"
        ? "Miniature Schnauzers can be prone to high blood-fat levels. We've excluded our Beef & Green Tripe blend from this match. The remaining options are not a low-fat prescription; if your dog has a fat restriction or pancreatitis history, check the formula with your veterinarian before feeding."
        : `Our breed-selection guide flags ${name} for extra care with dietary fat. We've excluded our Beef & Green Tripe blend from this match. The remaining options are not a low-fat prescription; if your dog has a fat restriction or pancreatitis history, check the formula with your veterinarian before feeding.`,
      copper: `Our breed guide notes a copper/liver consideration for ${name}. This quiz does not assess an individual copper restriction or verify a low-copper formula. If your dog has a liver condition or prescribed restriction, check the formula with your veterinarian before feeding.`,
      urate: `Our breed guide notes a purine/urate consideration for ${name}. This quiz does not assess an individual purine restriction or verify a low-purine formula. If your dog has a stone history or prescribed restriction, check the formula with your veterinarian before feeding.`,
      epi: `Our breed guide includes a digestion/EPI consideration for ${name}. A breed flag does not diagnose EPI, and this quiz does not assess condition-specific feeding needs. If your dog has digestive symptoms or a diagnosed condition, check the feeding plan with your veterinarian.`,
    };
    return {
      flags,
      excludedProteins: fatRestricted ? ["beef"] : [],
      preferredRoles: fatRestricted ? ["turkey", "variety", "salmon"] : [],
      notices: flags.map((flag) => ({ id: `breed_${flag}`, title: `A note for ${name}`, message: messages[flag] })),
    };
  }

  function getEffectiveAvoidances(customerAvoidances, profileOrResult) {
    const source = profileOrResult || {};
    const profile = source.breedProfile || source;
    return unique([
      ...normaliseAvoidances(customerAvoidances),
      ...asArray(source.effectiveAvoidances).map(normaliseProtein).filter(Boolean),
      ...resolveBreedRules(profile).excludedProteins,
    ]);
  }

  function buildTransitionPlan(state) {
    const source = state || {};
    if (healthClassification(source.health).selected.length) return {
      kind: "health_guidance", title: "Your transition needs individual guidance",
      message: "You noted a health or life-stage consideration. Review any feeding change with your veterinarian; the quiz's portion estimate is not an instruction to start a raw-food transition.", stages: [],
    };
    if (normaliseRawExperience(source.rawStatus || source.raw_experience) === "regularly") return {
      kind: "already_raw", title: "Keep your current routine in view",
      message: "Already feeding raw? You do not need the standard switch-from-current-food schedule. Introduce a new blend gradually and adjust by observation.", stages: [],
    };
    return {
      kind: "gradual", title: "A gradual transition",
      message: "For a dog ready to make a feeding change, use a 10-day transition and reach full raw on day 11. Keep the rest of each meal as the current food, and slow the change if needed.",
      stages: [{ days: "Days 1–3", percent: 25 }, { days: "Days 4–7", percent: 50 }, { days: "Days 8–10", percent: 75 }, { days: "Day 11 onward", percent: 100 }],
    };
  }

  function validWeight(value) {
    const number = Number(value);
    return Number.isFinite(number) && number >= 2 && number <= 250 ? number : null;
  }

  function normaliseBodyCondition(value) {
    const bodyCondition = canonicalValue(value);
    if (["underweight", "ideal", "overweight"].includes(bodyCondition)) return bodyCondition;
    return null;
  }

  function normaliseActivity(value) {
    const activity = canonicalValue(value);
    return ["low", "moderate", "high"].includes(activity) ? activity : null;
  }

  const QUESTION_REQUIREMENT_TARGETS = Object.freeze({
    name: { target: "#RDFitDogName", region: '[data-rd-fit-required-region="name"]' },
    birthday: { target: "#RDFitBirthday", region: '[data-rd-fit-required-region="birthday"]' },
    breed: { target: "#RDFitBreed", region: '[data-rd-fit-required-region="breed"]' },
    currentWeight: { target: "#RDFitWeight", region: '[data-rd-fit-required-region="currentWeight"]' },
    bodyCondition: { target: '[data-choice-field="bodyCondition"]', region: '[data-rd-fit-required-region="bodyCondition"]' },
    activity: { target: '[data-choice-field="activity"]', region: '[data-rd-fit-required-region="activity"]' },
    targetWeight: { target: "#RDFitTargetWeight", region: '[data-rd-fit-required-region="targetWeight"]' },
    proteins: { target: '[data-choice-field="proteins"]', region: '[data-rd-fit-required-region="proteins"]' },
    health: { target: '[data-choice-field="health"]', region: '[data-rd-fit-required-region="health"]' },
  });

  function completeQuestionState(key) {
    const requirement = key ? QUESTION_REQUIREMENT_TARGETS[key] : null;
    return {
      complete: true,
      state: "complete",
      target: requirement ? requirement.target : null,
      region: requirement ? requirement.region : null,
      message: "",
    };
  }

  function incompleteQuestionState(key, state, message, targetOverride) {
    const requirement = QUESTION_REQUIREMENT_TARGETS[key] || {};
    return {
      complete: false,
      state,
      target: targetOverride || requirement.target || null,
      region: requirement.region || null,
      message,
    };
  }

  function highestAdvisoryLevel(advisories) {
    const priority = { estimate: 1, pending: 1, conditional: 2, wait: 3 };
    return advisories.reduce((highest, advisory) => (
      !highest || priority[advisory.level] > priority[highest] ? advisory.level : highest
    ), null);
  }

  function ageQuestionAdvisories(source, config) {
    const asOf = config.asOf || source.ageAsOf || source.age_as_of || DEFAULT_AS_OF();
    const birthday = validateBirthday(source, { asOf });
    if (!birthday.valid) return [];
    if (birthday.birthdayPrecision === "unknown") {
      return [{
        code: "birthday_unknown",
        level: "estimate",
        title: "Age note",
        body: "We will use the current weight for an estimate because age is unknown.",
        targetValue: "unknown",
      }];
    }

    const reviewedBreeds = config.reviewedBreeds || config.breedData || config.breeds;
    const taxonomy = config.taxonomy || config.groupData;
    // Generic Mixed Breed / Unknown thresholds become size-specific only after
    // the weight answer itself passes the quiz's accepted range.
    const profile = deriveBreedProfile(source.breed, validWeight(source.currentWeight), taxonomy, reviewedBreeds);
    const age = deriveAge({ ...source, ...birthday }, profile, { asOf });
    if (!age.valid) return [];
    if (age.isPuppy) {
      return [{
        code: "puppy",
        level: "estimate",
        title: "Growing dog note",
        body: "Growing dogs have individual feeding needs; this is a current-weight estimate.",
        targetValue: birthday.birthdayPrecision,
      }];
    }
    return [];
  }

  function healthQuestionAdvisories(health) {
    const rawValues = normaliseSelection(health, "none").filter((value) => value !== "none");
    if (!rawValues.length) return [];
    return [{
      code: "health_profile_note",
      level: "estimate",
      title: "Profile note",
      body: "Medical conditions, pregnancy, nursing and prescribed diets may change feeding needs. Continue for an estimate; ask your veterinarian for individual guidance.",
      targetValue: rawValues[0],
    }];
  }

  /**
   * Pure UI readiness contract. `step` is the zero-based quiz step used by the
   * presentation state. It mirrors the existing required-answer gates while
   * deriving local-only safety notices that never alter recommendation output.
   */
  function evaluateQuestionState(state, step, context) {
    const config = isPlainObject(context) ? context : {};
    const drafts = isPlainObject(config.drafts) ? config.drafts : {};
    const source = { ...createDefaultState(), ...(isPlainObject(state) ? state : {}), ...drafts };
    const parsedStep = Number(step ?? source.step);
    const question = Number.isInteger(parsedStep) ? parsedStep : -1;
    let evaluation;

    if (question === 0) {
      evaluation = toText(source.name)
        ? completeQuestionState("name")
        : incompleteQuestionState("name", "missing", "Add Dawg's name to continue.");
    } else if (question === 1) {
      const precision = canonicalValue(source.birthdayPrecision);
      const targetByPrecision = {
        exact_day: "#RDFitBirthday",
        month_only: "#RDFitBirthdayMonth",
        approximate_age: "#RDFitEstimatedAge",
        unknown: '[data-choice-field="birthdayPrecision"][data-choice-value="unknown"]',
      };
      const target = targetByPrecision[precision] || '[data-choice-field="birthdayPrecision"]';
      const rawValue = precision === "approximate_age" ? source.estimatedAgeMonths : source.birthday;
      const missing = precision !== "unknown" && !toText(rawValue);
      const birthday = validateBirthday(source, { asOf: config.asOf || source.ageAsOf || source.age_as_of || DEFAULT_AS_OF() });
      evaluation = birthday.valid
        ? { ...completeQuestionState("birthday"), target }
        : incompleteQuestionState("birthday", missing ? "missing" : "invalid", birthday.error || "Add a valid birthday or approximate age.", target);
    } else if (question === 2) {
      const identity = stableBreedIdentity(source.breed);
      if (!identity) {
        evaluation = toText(drafts.breedQuery)
          ? incompleteQuestionState("breed", "invalid", "Choose a breed, Mixed Breed, or Unknown from the reviewed list.")
          : incompleteQuestionState("breed", "missing", "Choose a breed, Mixed Breed, or Unknown from the list.");
      } else {
        const profile = deriveBreedProfile(
          source.breed,
          source.currentWeight,
          config.taxonomy || config.groupData,
          config.reviewedBreeds || config.breedData || config.breeds,
        );
        evaluation = profile.valid && profile.reviewed
          ? completeQuestionState("breed")
          : incompleteQuestionState("breed", "invalid", "Choose a breed, Mixed Breed, or Unknown from the reviewed list.");
      }
    } else if (question === 3) {
      const currentText = toText(source.currentWeight);
      if (!currentText) {
        evaluation = incompleteQuestionState("currentWeight", "missing", "Add a current weight between 2 and 250 pounds.");
      } else if (!validWeight(source.currentWeight)) {
        evaluation = incompleteQuestionState("currentWeight", "invalid", "Add a current weight between 2 and 250 pounds.");
      } else if (!normaliseBodyCondition(source.bodyCondition || source.body_condition)) {
        evaluation = incompleteQuestionState("bodyCondition", "missing", "Choose underweight, ideal, or overweight to continue.");
      } else {
        evaluation = completeQuestionState("currentWeight");
      }
    } else if (question === 4) {
      if (!toText(source.activity)) {
        evaluation = incompleteQuestionState("activity", "missing", "Choose low, moderate, or high activity to continue.");
      } else if (!normaliseActivity(source.activity)) {
        evaluation = incompleteQuestionState("activity", "invalid", "Choose low, moderate, or high activity to continue.");
      } else {
        evaluation = completeQuestionState("activity");
      }
    } else if (question === 6) {
      const answered = Array.isArray(source.proteins) && normaliseSelection(source.proteins, "none").length > 0;
      evaluation = answered
        ? completeQuestionState("proteins")
        : incompleteQuestionState("proteins", "missing", "Choose No known avoidances or select every protein that needs to stay out.");
    } else if (question === 7) {
      const answered = Array.isArray(source.health) && normaliseSelection(source.health, "none").length > 0;
      evaluation = answered
        ? completeQuestionState("health")
        : incompleteQuestionState("health", "missing", "Choose None of these apply or select every condition that applies.");
    } else if (question >= 0 && question < TOTAL_STEPS) {
      evaluation = completeQuestionState(null);
    } else {
      evaluation = incompleteQuestionState(null, "invalid", "This quiz step is not available.");
    }

    const advisories = question >= 1 && question <= 3
      ? ageQuestionAdvisories(source, config)
      : (question === 7 ? healthQuestionAdvisories(source.health) : []);
    return {
      ...evaluation,
      highestSafetyLevel: highestAdvisoryLevel(advisories),
      advisories,
    };
  }

  function roundToTenth(value) {
    return Number(Number(value).toFixed(1));
  }

  function roundUpTenth(value) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) return null;
    const scaled = numeric * 10;
    const tolerance = Number.EPSILON * Math.max(1, Math.abs(scaled)) * 8;
    return Math.ceil(scaled - tolerance) / 10;
  }

  function compareBoxPlans(candidate, current) {
    if (!current) return -1;
    if (candidate.count !== current.count) return candidate.count - current.count;
    if (Math.abs(candidate.overage - current.overage) > FLOATING_POINT_TOLERANCE) {
      return candidate.overage - current.overage;
    }
    const thirtyDifference = (current.quantities[30] || 0) - (candidate.quantities[30] || 0);
    if (thirtyDifference !== 0) return thirtyDifference;
    const fourteenDifference = (current.quantities[14] || 0) - (candidate.quantities[14] || 0);
    if (fourteenDifference !== 0) return fourteenDifference;
    return (candidate.quantities[8] || 0) - (current.quantities[8] || 0);
  }

  function boxPlanCandidate(required, quantities) {
    const total = BOX_SIZES.reduce((sum, size) => sum + (Number(quantities[size]) || 0) * size, 0);
    return {
      total,
      count: BOX_SIZES.reduce((sum, size) => sum + (Number(quantities[size]) || 0), 0),
      overage: Math.max(0, total - required),
      shortfall: Math.max(0, required - total),
      quantities: { 8: Number(quantities[8]) || 0, 14: Number(quantities[14]) || 0, 30: Number(quantities[30]) || 0 },
    };
  }

  function calculateBoxPlan(requiredPounds, availableBoxSizes) {
    const required = Number(requiredPounds);
    const sizes = unique(asArray(availableBoxSizes || BOX_SIZES)
      .map(Number)
      .filter((size) => [8, 14, 30].includes(size)))
      .sort((left, right) => right - left);
    if (!Number.isFinite(required) || required <= 0 || !sizes.length) return null;

    let best = null;
    const lowerPlansByCount = new Map();
    const quantities = { 8: 0, 14: 0, 30: 0 };

    function search(index) {
      if (index === sizes.length) {
        const candidate = boxPlanCandidate(required, quantities);
        if (!candidate.count) return;
        if (candidate.shortfall <= FLOATING_POINT_TOLERANCE) {
          if (compareBoxPlans(candidate, best) < 0) best = candidate;
          return;
        }
        const currentLowerPlan = lowerPlansByCount.get(candidate.count);
        const isCloserLowerPlan = !currentLowerPlan
          || candidate.total > currentLowerPlan.total + FLOATING_POINT_TOLERANCE;
        const isPreferredEqualPlan = currentLowerPlan
          && Math.abs(candidate.total - currentLowerPlan.total) <= FLOATING_POINT_TOLERANCE
          && compareBoxPlans(candidate, currentLowerPlan) < 0;
        if (isCloserLowerPlan || isPreferredEqualPlan) lowerPlansByCount.set(candidate.count, candidate);
        return;
      }
      const size = sizes[index];
      const maxCount = Math.ceil(required / size) + 1;
      for (let quantity = 0; quantity <= maxCount; quantity += 1) {
        quantities[size] = quantity;
        search(index + 1);
      }
      quantities[size] = 0;
    }

    search(0);
    if (!best) return null;
    const lowerPlan = [...lowerPlansByCount.values()]
      .filter((candidate) => candidate.count <= best.count)
      .reduce((current, candidate) => {
        if (!current || candidate.total > current.total + FLOATING_POINT_TOLERANCE) return candidate;
        if (Math.abs(candidate.total - current.total) <= FLOATING_POINT_TOLERANCE
          && compareBoxPlans(candidate, current) < 0) return candidate;
        return current;
      }, null);
    // An exact covering order is already the cleanest possible answer. Never
    // trade it down merely because a smaller shipment fits within 15 percent.
    const exactCover = best.overage <= FLOATING_POINT_TOLERANCE;
    const useLowerPlan = !exactCover
      && Boolean(lowerPlan)
      && required <= (lowerPlan.total * (1 + LOWER_ORDER_TOLERANCE)) + FLOATING_POINT_TOLERANCE;
    const selected = useLowerPlan ? lowerPlan : best;
    const overagePounds = roundToTenth(selected.overage);
    const shortfallPounds = roundToTenth(selected.shortfall);
    return {
      ...selected,
      // Existing result consumers use `over`/`bufferPounds` as a non-negative
      // overage. Keep that contract while exposing the signed difference and
      // shortfall needed for the customer-facing lower-order explanation.
      over: overagePounds,
      overagePounds,
      shortfallPounds,
      differencePounds: roundToTenth(selected.total - required),
      isLowerOrder: useLowerPlan,
    };
  }

  function calculateCadenceBoxPlan(exactDailyStartingPounds, cadenceWeeks, availableBoxSizes) {
    const exactDailyPounds = Number(exactDailyStartingPounds);
    const cadence = normaliseCadenceWeeks(cadenceWeeks);
    if (!Number.isFinite(exactDailyPounds) || exactDailyPounds <= 0 || !cadence) return null;
    const shipmentDays = cadence * 7;
    const requiredPoundsPerShipment = roundUpTenth(exactDailyPounds * shipmentDays);
    const shipmentPlan = calculateBoxPlan(requiredPoundsPerShipment, availableBoxSizes);
    if (!shipmentPlan) return null;
    const shipmentsPer28Days = 28 / shipmentDays;
    const requiredPounds28Days = roundUpTenth(exactDailyPounds * 28);
    const deliveredPoundsPerShipment = shipmentPlan.total;
    const deliveredPounds28Days = roundToTenth(deliveredPoundsPerShipment * shipmentsPer28Days);
    const differencePounds28Days = roundToTenth(deliveredPounds28Days - requiredPounds28Days);
    const shortfallPounds28Days = Math.max(0, differencePounds28Days * -1);
    const daysNotCoveredPerShipment = shipmentPlan.shortfallPounds > FLOATING_POINT_TOLERANCE
      ? Math.ceil((shipmentPlan.shortfallPounds / exactDailyPounds) - FLOATING_POINT_TOLERANCE)
      : 0;
    const daysNotCovered28Days = shortfallPounds28Days > FLOATING_POINT_TOLERANCE
      ? Math.ceil((shortfallPounds28Days / exactDailyPounds) - FLOATING_POINT_TOLERANCE)
      : 0;
    return {
      ...shipmentPlan,
      cadenceWeeks: cadence,
      shipmentDays,
      shipmentsPer28Days,
      exactDailyStartingPounds: exactDailyPounds,
      requiredPounds: requiredPoundsPerShipment,
      requiredPoundsPerShipment,
      requiredPounds28Days,
      deliveredPounds: deliveredPoundsPerShipment,
      deliveredPoundsPerShipment,
      deliveredPounds28Days,
      differencePounds28Days,
      shortfallPounds28Days,
      overagePounds28Days: Math.max(0, differencePounds28Days),
      coverageDaysPerShipment: roundToTenth(deliveredPoundsPerShipment / exactDailyPounds),
      coverageDays28: roundToTenth(deliveredPounds28Days / exactDailyPounds),
      daysNotCoveredPerShipment,
      daysNotCovered28Days,
    };
  }

  function recommendCadenceWeeks(exactDailyStartingPounds, fourWeekBoxPlan, availableBoxSizes) {
    if (!isPlainObject(fourWeekBoxPlan)
      || Number(fourWeekBoxPlan.quantities && fourWeekBoxPlan.quantities[30]) !== 2
      || Number(fourWeekBoxPlan.quantities && fourWeekBoxPlan.quantities[14]) !== 0
      || Number(fourWeekBoxPlan.quantities && fourWeekBoxPlan.quantities[8]) !== 0) return 4;
    const twoWeekPlan = calculateCadenceBoxPlan(exactDailyStartingPounds, 2, availableBoxSizes);
    if (!twoWeekPlan
      || Number(twoWeekPlan.quantities && twoWeekPlan.quantities[30]) !== 1
      || Number(twoWeekPlan.quantities && twoWeekPlan.quantities[14]) !== 0
      || Number(twoWeekPlan.quantities && twoWeekPlan.quantities[8]) !== 0) return 4;
    // Four weeks is the approved default. The one high-volume exception splits
    // exactly two 30 lb boxes into one verified 30 lb shipment every two weeks.
    return twoWeekPlan.deliveredPounds28Days <= Number(fourWeekBoxPlan.total) + FLOATING_POINT_TOLERANCE
      ? 2
      : 4;
  }

  function feedingPlan(state, route, profile, availableBoxSizes) {
    const currentWeight = validWeight(state.currentWeight);
    const bodyCondition = normaliseBodyCondition(state.bodyCondition || state.body_condition, state.goal);
    const activity = normaliseActivity(state.activity);
    if (!currentWeight || !bodyCondition || !activity) return null;
    // The estimate always starts from the observed current weight. Body
    // condition replaces target-weight arithmetic: underweight 3%, ideal
    // 2.5%, overweight 2%, then activity adjusts by a quarter percent.
    const baseWeight = currentWeight;
    const goalRate = ({ underweight: 3, ideal: 2.5, overweight: 2 })[bodyCondition];
    const activityAdjust = ({ low: -0.25, moderate: 0, high: 0.25 })[activity];
    const percent = goalRate + activityAdjust;
    // Preserve the displayed daily-ounce formula. Packaging deliberately uses
    // the exact daily starting amount so a display rounding step cannot alter
    // the 28-day requirement or understate a shortfall.
    const exactDailyStartingPounds = baseWeight * (percent / 100);
    const dailyOz = Math.round(exactDailyStartingPounds * 16 * 10) / 10;
    const fourWeekPounds = roundUpTenth(exactDailyStartingPounds * 28);
    // V3 always gives every feeder the whole four-week quantity. Transition
    // guidance changes, but it no longer truncates the subscription order.
    const starterDays = 28;
    const starterPounds = fourWeekPounds;
    const neededPounds = fourWeekPounds;
    const boxPlan = calculateBoxPlan(neededPounds, availableBoxSizes);
    if (!boxPlan) return null;
    const mealsPerDay = normaliseMealsPerDay(state.mealsPerDay || state.meals_per_day);
    const perMealOz = Number((dailyOz / mealsPerDay).toFixed(1));
    const coverageDays = roundToTenth(boxPlan.total / exactDailyStartingPounds);
    const bufferPounds = boxPlan.overagePounds;
    const bufferOz = Math.round(bufferPounds * 16 * 10) / 10;
    const bufferDays = Number(Math.max(0, coverageDays - 28).toFixed(1));
    const daysNotCovered = boxPlan.shortfallPounds > FLOATING_POINT_TOLERANCE
      ? Math.ceil((boxPlan.shortfallPounds / exactDailyStartingPounds) - FLOATING_POINT_TOLERANCE)
      : 0;
    const enrichedBoxPlan = {
      ...boxPlan,
      requiredPounds: neededPounds,
      requiredPounds28Days: neededPounds,
      exactDailyStartingPounds,
      deliveredPounds: boxPlan.total,
      coverageDays,
      bufferPounds,
      bufferOz,
      bufferDays,
      daysNotCovered,
    };
    const cadenceBoxPlans = {
      2: calculateCadenceBoxPlan(exactDailyStartingPounds, 2, availableBoxSizes),
      4: calculateCadenceBoxPlan(exactDailyStartingPounds, 4, availableBoxSizes),
    };
    const recommendedCadenceWeeks = recommendCadenceWeeks(exactDailyStartingPounds, enrichedBoxPlan, availableBoxSizes);
    return {
      baseWeight,
      bodyCondition,
      activity,
      percent,
      dailyOz,
      exactDailyStartingPounds,
      fourWeekPounds,
      starterDays,
      starterPounds,
      neededPounds,
      mealsPerDay,
      perMealOz,
      boxPlan: enrichedBoxPlan,
      cadenceBoxPlans,
      recommendedCadenceWeeks,
      smallBoxPreferenceApplied: ["Toy", "Small"].includes(profile.size) && neededPounds <= 8 && enrichedBoxPlan.total === 8,
    };
  }

  function noProductResult(context) {
    const breedRules = resolveBreedRules(context.breedProfile);
    const breedNotices = context.breedNotices || breedRules.notices;
    return {
      status: context.status || "no_sale",
      calculationRevision: CALCULATION_REVISION,
      safetyLevel: context.safetyLevel || "no_sale",
      route: context.route,
      qualificationCategory: context.qualificationCategory || "review_required",
      reason: context.reason,
      reasons: unique([...breedNotices.map((notice) => notice.message), ...(context.reasons || [context.reason].filter(Boolean))]),
      holds: context.holds || [],
      productRole: null,
      role: null,
      requiredProteins: [],
      purchaseAllowed: false,
      guardedPurchaseAllowed: false,
      requiresAcknowledgement: false,
      acknowledgementReceived: false,
      selectionRequired: false,
      compatibleRoles: context.compatibleRoles || [],
      alternativeRole: null,
      recommendationScores: {},
      purchaseType: context.purchaseType || "subscription",
      sellingPlan: null,
      pricing: null,
      subscriptionPlanStatus: context.subscriptionPlanStatus || "unavailable",
      needsCartValidation: false,
      dailyOz: null,
      exactDailyStartingPounds: null,
      percent: null,
      fourWeekPounds: null,
      neededPounds: null,
      mealsPerDay: null,
      perMealOz: null,
      boxPlan: null,
      cadenceBoxPlans: null,
      recommendedCadenceWeeks: 4,
      age: context.age || null,
      breedProfile: context.breedProfile || null,
      avoidances: context.avoidances || [],
      effectiveAvoidances: getEffectiveAvoidances(context.avoidances, context),
      conditionState: context.conditionState || null,
      dietWatchFlags: context.dietWatchFlags || breedRules.flags,
      breedNotices,
      cartValidation: context.cartValidation || { valid: null, reason: "not_applicable", violations: [] },
    };
  }

  function roleDisplayName(role) {
    return ({ beef: "Beef", turkey: "Turkey", salmon: "Turkey & Salmon", variety: "Variety" })[role] || "the compatible blend";
  }

  function primaryOutcome(state) {
    const direct = canonicalValue(state.primaryOutcome || state.primary_outcome || state.outcome || "");
    if (direct) return direct;
    return normaliseSelection(state.outcomes, "none").find((value) => value !== "none") || "";
  }

  function normaliseRawExperience(value) {
    const raw = canonicalValue(value);
    if (["regularly", "established", "experienced", "daily"].includes(raw)) return "regularly";
    if (["sometimes", "occasional", "occasionally", "topper"].includes(raw)) return "sometimes";
    return "not_yet";
  }

  function rolePlanFor(role, source, route, profile, avoidances, config) {
    let availableBoxSizes = config.availableBoxSizes;
    let varietyInfo = null;
    let inventory = null;
    if (role === "variety") {
      varietyInfo = varietyEligibility(avoidances, config, config.formulas);
      if (!varietyInfo.sizes.length) return { valid: false, reason: "no_compatible_variety_variant", varietyInfo };
      availableBoxSizes = varietyInfo.sizes;
    } else if (hasSuppliedVariantCatalog(config)) {
      inventory = verifiedRoleInventory(role, avoidances, config);
      if (!inventory.sizes.length) return { valid: false, reason: "four_week_subscription_unavailable", inventory };
      availableBoxSizes = inventory.sizes;
    }
    const feeding = feedingPlan(source, route, profile, availableBoxSizes);
    if (!feeding) return { valid: false, reason: "box_coverage_unavailable", varietyInfo, inventory };
    const inventoryCheck = verifyBoxPlanInventory(role, feeding.boxPlan, avoidances, config);
    if (!inventoryCheck.valid) {
      return {
        valid: false,
        reason: hasSuppliedVariantCatalog(config) ? "four_week_subscription_unavailable" : "box_plan_inventory_unavailable",
        varietyInfo,
        inventory,
        feeding,
        inventoryCheck,
      };
    }
    return {
      valid: true,
      role,
      feeding,
      varietyInfo,
      inventory,
      inventoryCheck,
      sellingPlan: inventoryCheck.sellingPlan || null,
      pricing: inventoryCheck.pricing || null,
      subscriptionPlanStatus: hasSuppliedVariantCatalog(config) ? "verified" : "unverified",
    };
  }

  function rolePlansFor(source, route, profile, avoidances, config) {
    return RECOMMENDATION_ROLE_ORDER.reduce((all, role) => {
      // Variety uses verified variant composition rather than the broad
      // three-protein role label. A salmon avoidance can still safely select
      // the reviewed Beef/Turkey paired mix, while a turkey avoidance cannot.
      if (role !== "variety" && !roleIsAllowed(role, avoidances, config.formulas)) return all;
      const plan = rolePlanFor(role, source, route, profile, avoidances, config);
      if (plan.valid) all[role] = plan;
      return all;
    }, {});
  }

  function recommendationLayers(source, profile) {
    const outcome = primaryOutcome(source);
    const tolerated = toleratedProteins(source.toleratedProtein || source.tolerated_protein || source.knownToleratedProtein || source.known_tolerated_protein);
    const rawExperience = normaliseRawExperience(source.rawStatus || source.raw_experience);
    const layers = [];
    if (["coat", "skin_coat", "skin_and_coat"].includes(outcome)) {
      layers.push({ route: "coat", score: 700, roles: ["salmon"], reason: "The selected coat goal makes Turkey & Salmon the first compatible match." });
    }
    if (canonicalValue(source.activity) === "high" && profile.workingOrSporting) {
      layers.push({ route: "working_sporting_high_activity", score: 600, roles: ["beef"], reason: "High activity plus the reviewed Working or Sporting breed group makes Beef the first compatible match." });
    }
    if (tolerated.length) {
      layers.push({ route: "known_tolerated_protein", score: 500, roles: tolerated, reason: "A known tolerated protein is the first compatible match after the higher-priority routes." });
    }
    if (rawExperience === "regularly") {
      layers.push({ route: "established_raw", score: 400, roles: ["variety"], reason: "An established raw routine keeps Variety as the compatible default." });
    } else {
      layers.push({ route: "new_or_occasional_raw", score: 400, roles: ["turkey"], reason: "A new or occasional raw routine starts with Turkey when it is compatible." });
    }
    return layers;
  }

  function selectRecommendation(source, profile, rolePlans) {
    const available = Object.keys(rolePlans);
    const scores = Object.fromEntries(available.map((role) => [role, 0]));
    for (const layer of recommendationLayers(source, profile)) {
      const candidates = layer.roles.filter((role) => rolePlans[role]);
      if (!candidates.length) continue;
      candidates.forEach((role) => { scores[role] = layer.score; });
      const ordered = RECOMMENDATION_ROLE_ORDER.filter((role) => candidates.includes(role));
      return {
        role: ordered[0],
        alternativeRole: ordered[1] || null,
        route: layer.route,
        reason: layer.reason,
        scores,
      };
    }
    return { role: null, alternativeRole: null, route: "no_compatible_product", reason: "No compatible formula remains after the selected protein filters.", scores };
  }

  function resultReasons(status, recommendation, feeding, breedNotices) {
    const notices = asArray(breedNotices).map((notice) => notice.message);
    const reasons = [];
    if (status === "choice_required") {
      reasons.push("Breed-related diet considerations keep the final formula choice in your hands after review.");
      reasons.push("Every option shown passed the protein-fit answers and verified ingredient checks.");
      reasons.push(feeding
        ? `The starting plan is ${feeding.dailyOz} oz per day, split into ${feeding.mealsPerDay} meal${feeding.mealsPerDay === 1 ? "" : "s"}.`
        : "Every option shown has a verified four-week subscription route.");
      return [...notices, ...reasons];
    }
    if (recommendation && recommendation.reason) reasons.push(recommendation.reason);
    reasons.push("The protein-fit answers were applied before this route was chosen.");
    if (feeding) reasons.push(`The starting plan is ${feeding.dailyOz} oz per day, split into ${feeding.mealsPerDay} meal${feeding.mealsPerDay === 1 ? "" : "s"}.`);
    return [...notices, ...reasons.slice(0, 3)];
  }

  function withCartValidation(result, cartLines, avoidances) {
    if (!Array.isArray(cartLines)) return result;
    const cartValidation = validateCartLines(cartLines, avoidances, result);
    if (cartValidation.valid) return { ...result, cartValidation };
    if (["wait", "conditional", "choice_required"].includes(result.status)) return { ...result, cartValidation };
    return noProductResult({
      status: "no_sale",
      route: "no_compatible_product",
      qualificationCategory: "no_compatible_product",
      safetyLevel: "no_sale",
      reason: "No available product variant can be verified against the proteins selected to avoid, so this quiz will not recommend a box.",
      holds: ["cart_composition_unverified"],
      age: result.age,
      breedProfile: result.breedProfile,
      avoidances,
      effectiveAvoidances: result.effectiveAvoidances,
      dietWatchFlags: result.dietWatchFlags,
      breedNotices: result.breedNotices,
      conditionState: result.conditionState,
      cartValidation,
    });
  }

  // Ingredient compatibility and actual box composition decide the estimate.
  function calculateResult(state, options) {
    const source = { ...createDefaultState(), ...(state || {}) };
    const config = { ...(isPlainObject(options) ? options : {}) };
    const catalog = resolveFormulaCatalog(config.formulaCatalog);
    const suppliedBodyCondition = normaliseBodyCondition(source.bodyCondition || source.body_condition, source.goal);
    const suppliedActivity = normaliseActivity(source.activity);
    source.bodyCondition = suppliedBodyCondition || toText(source.bodyCondition || source.body_condition);
    source.goal = ({ underweight: "gain", ideal: "maintain", overweight: "lose" })[suppliedBodyCondition] || source.goal;
    source.targetWeight = "";
    config.formulas = catalog.formulas;
    const reviewedBreeds = config.reviewedBreeds || config.breedCatalog || config.breeds;
    const profile = rehydrateBreedSelection(source.breed, reviewedBreeds, source.currentWeight, config.taxonomy);
    const age = config.age || deriveAge(source, profile, { asOf: config.asOf || source.ageAsOf || DEFAULT_AS_OF() });
    const avoidanceInput = avoidanceSelection(source.proteins);
    const avoidances = avoidanceInput.avoidances;
    const breedRules = resolveBreedRules(profile);
    const effectiveAvoidances = getEffectiveAvoidances(avoidances, profile);
    const health = healthClassification(source.health);
    const proteinFitAnswered = normaliseSelection(source.proteins, "none").length > 0;
    const watchedBreedFlags = breedRules.flags;
    const purchaseType = normalisePurchaseType(source.purchaseType || source.purchase_type);
    const hold = (status, route, qualificationCategory, reason, holds, safetyLevel) => noProductResult({ status, route, qualificationCategory, reason, holds, safetyLevel, age, breedProfile: profile, avoidances, effectiveAvoidances, conditionState: health, dietWatchFlags: watchedBreedFlags, purchaseType });

    if (!catalog.valid) return hold("no_sale", "formula_catalog_unverified", "input_required", "The formula ingredient catalog could not be verified, so no product is recommended.", ["formula_catalog_unverified"], "no_sale");
    if (avoidanceInput.unknown.length) return hold("no_sale", "unknown_protein_avoidance", "input_required", "Choose only the listed protein avoidances before this quiz builds a recommendation.", ["unknown_protein_avoidance"], "no_sale");
    if (source.bodyConditionReconfirmationPending === true) return hold("no_sale", "body_condition_reconfirmation", "input_required", "Confirm the current weight and choose a body condition to update this plan.", ["body_condition_reconfirmation"], "no_sale");
    if (!suppliedBodyCondition) return hold("no_sale", "body_condition_invalid", "input_required", "Choose underweight, ideal, or overweight before this quiz builds an estimate.", ["body_condition_invalid"], "no_sale");
    if (!suppliedActivity) return hold("no_sale", "activity_invalid", "input_required", "Choose low, moderate, or high activity before this quiz builds an estimate.", ["activity_invalid"], "no_sale");

    if (!proteinFitAnswered || !health.answered) {
      const missing = [!proteinFitAnswered ? "protein_fit_unanswered" : null, !health.answered ? "conditions_unanswered" : null].filter(Boolean);
      return hold(
        "no_sale",
        "required_answers_missing",
        "input_required",
        "Confirm the protein-fit and safety-check answers before this quiz builds a recommendation.",
        missing,
        "no_sale",
      );
    }

    if (!profile.valid || !profile.reviewed) {
      return hold("no_sale", "breed_review_required", "input_required", "The selected breed could not be matched to the current reviewed breed list. Please choose a reviewed breed, Mixed Breed, or Unknown before continuing.", [profile.reviewReason || "breed_unverified"], "no_sale");
    }
    if (!age.valid) return hold("no_sale", "birthday_invalid", "input_required", "A valid birthday or age estimate is needed before this quiz builds an estimate.", ["birthday_invalid"], "no_sale");
    if (!validWeight(source.currentWeight)) {
      return hold("no_sale", "weight_input_required", "input_required", "A current weight between 2 and 250 pounds is needed before this quiz can calculate a starting amount.", ["weight_input_required"], "no_sale");
    }
    const planningRoute = "estimated_product_match";
    const plans = rolePlansFor(source, planningRoute, profile, effectiveAvoidances, config);
    const compatibleRoles = RECOMMENDATION_ROLE_ORDER.filter((role) => plans[role]);
    const ingredientEligibleFormulaRoles = FORMULA_ROLES.filter((role) => roleIsAllowed(role, effectiveAvoidances, config.formulas));
    if (!compatibleRoles.length) {
      const ingredientBlocked = ingredientEligibleFormulaRoles.length === 0;
      return hold(
        "no_sale",
        ingredientBlocked ? "no_compatible_ingredients" : "no_compatible_product",
        "no_compatible_product",
        ingredientBlocked
          ? "No available formula remains after the selected ingredient restrictions were applied."
          : "No compatible formula has a verified four-week subscription plan at the available box sizes.",
        [ingredientBlocked ? "no_compatible_ingredients" : "four_week_subscription_unavailable"],
        "no_sale",
      );
    }

    const eligibleFormulaRoles = FORMULA_ROLES.filter((role) => plans[role]);
    const preferredSingleRole = ["turkey", "beef", "salmon"].find((role) => plans[role]) || null;
    const recommendation = breedRules.excludedProteins.includes("beef") && plans.turkey
      ? {
        role: "turkey",
        alternativeRole: null,
        route: "breed_fat_turkey_priority",
        reason: "Turkey is the first ingredient-compatible match under this breed's product-selection rule.",
        scores: { turkey: 1000 },
      }
      // A verified mixed box has its own compatible composition and selling
      // plan; its availability does not depend on standalone blend plans.
      : plans.variety
      ? {
        role: "variety",
        alternativeRole: null,
        route: "compatible_variety",
        reason: "More than one compatible blend is available, so this estimated plan includes bowl variety without adding an excluded protein.",
        scores: { variety: 900 },
      }
      : {
        role: preferredSingleRole,
        alternativeRole: null,
        route: breedRules.excludedProteins.includes("beef") ? "breed_fat_compatible_fallback" : "compatible_single",
        reason: breedRules.excludedProteins.includes("beef") && preferredSingleRole === "turkey"
          ? "Turkey is the first ingredient-compatible match under this breed's product-selection rule."
          : (eligibleFormulaRoles.length === 1
            ? `Only ${roleDisplayName(preferredSingleRole)} can be matched to the selected ingredient restrictions and available box options.`
            : `${roleDisplayName(preferredSingleRole)} is the first compatible match because no verified compatible mixed box is available.`),
        scores: preferredSingleRole ? { [preferredSingleRole]: 800 } : {},
      };
    if (!recommendation.role || !plans[recommendation.role]) {
      return hold("no_sale", "no_compatible_product", "no_compatible_product", "No compatible formula remains for this profile.", ["no_compatible_product"], "no_sale");
    }

    const role = recommendation.role;
    const recommendationRoute = recommendation.route;
    const alternativeRole = recommendation.alternativeRole;
    const plan = role ? plans[role] : null;
    const lifecycleNotes = unique([
      ...(age.eligibilityUnknown ? ["age_unknown_estimate"] : (age.isPuppy ? ["growing_dog_estimate"] : [])),
      ...(age.isSenior ? ["senior_estimate"] : []),
      ...health.selected.map((condition) => `health_note:${condition}`),
    ]);
    const status = "ready";
    const route = recommendationRoute;
    const qualificationCategory = lifecycleNotes.length ? "estimate_with_profile_notes" : "standard_adult";
    const reasons = resultReasons(status, recommendation, plan && plan.feeding, breedRules.notices);
    const result = {
      status,
      calculationRevision: CALCULATION_REVISION,
      safetyLevel: status === "ready" ? (recommendationRoute === "senior_conservative" ? "senior" : "ready") : status,
      route,
      recommendationRoute,
      qualificationCategory,
      reason: recommendation.reason,
      reasons,
      holds: [],
      productRole: role,
      role,
      requiredProteins: role === "variety" && plan && plan.varietyInfo
        ? unique(Object.values(plan.varietyInfo.bySize).flatMap((entry) => entry.proteins))
        : (role ? [...ROLE_PROTEINS[role]] : []),
      purchaseAllowed: Boolean(plan),
      guardedPurchaseAllowed: false,
      requiresAcknowledgement: false,
      acknowledgementReceived: false,
      selectionRequired: false,
      compatibleRoles,
      dietWatchFlags: watchedBreedFlags,
      breedNotices: breedRules.notices,
      effectiveAvoidances,
      formulaCatalogSource: catalog.source,
      lifecycleNotes,
      alternativeRole,
      recommendationScores: recommendation.scores,
      purchaseType,
      sellingPlan: plan ? plan.sellingPlan : null,
      pricing: plan ? plan.pricing : null,
      subscriptionPlanStatus: plan ? plan.subscriptionPlanStatus : (hasSuppliedVariantCatalog(config) ? "unavailable" : "unverified"),
      variantSelections: plan && plan.inventoryCheck && Array.isArray(plan.inventoryCheck.selections)
        ? plan.inventoryCheck.selections.map((selection) => ({
          size: selection.size,
          quantity: selection.quantity,
          variantId: selection.variant.id,
          proteinComposition: selection.proteinComposition,
          sellingPlanId: selection.sellingPlan ? String(selection.sellingPlan.id) : null,
        }))
        : [],
      needsCartValidation: Boolean(role),
      dailyOz: plan ? plan.feeding.dailyOz : null,
      baseWeight: plan ? plan.feeding.baseWeight : null,
      exactDailyStartingPounds: plan ? plan.feeding.exactDailyStartingPounds : null,
      percent: plan ? plan.feeding.percent : null,
      fourWeekPounds: plan ? plan.feeding.fourWeekPounds : null,
      starterDays: plan ? plan.feeding.starterDays : null,
      starterPounds: plan ? plan.feeding.starterPounds : null,
      neededPounds: plan ? plan.feeding.neededPounds : null,
      mealsPerDay: plan ? plan.feeding.mealsPerDay : null,
      perMealOz: plan ? plan.feeding.perMealOz : null,
      boxPlan: plan ? plan.feeding.boxPlan : null,
      cadenceBoxPlans: plan ? plan.feeding.cadenceBoxPlans : null,
      recommendedCadenceWeeks: plan ? plan.feeding.recommendedCadenceWeeks : 4,
      smallBoxPreferenceApplied: plan ? plan.feeding.smallBoxPreferenceApplied : false,
      variantEligibility: role === "variety" && plan && plan.varietyInfo ? plan.varietyInfo.bySize : null,
      age,
      breedProfile: profile,
      avoidances,
      conditionState: health,
      cartValidation: { valid: null, reason: "not_evaluated", violations: [] },
    };
    return withCartValidation(result, config.cartLines, effectiveAvoidances);
  }

  function normaliseVariantLabel(value) {
    return toText(value)
      .toLowerCase()
      .replace(/[–—]/g, "-")
      .replace(/\s+/g, " ")
      .trim();
  }

  function explicitComposition(value) {
    const raw = asArray(value);
    if (!raw.length) return null;
    const proteins = [];
    for (const item of raw) {
      const label = normaliseVariantLabel(item);
      if (KNOWN_PROTEINS.includes(label)) proteins.push(label);
      else if (label === "turkey & salmon") proteins.push("turkey", "salmon");
      else return null;
    }
    return unique(proteins);
  }

  function explicitCompositionMetadata(variant) {
    const fields = ["proteinComposition", "proteins", "composition"].filter((field) => hasOwn(variant, field));
    if (!fields.length) return { provided: false, valid: true, proteins: [] };
    const compositions = fields.map((field) => explicitComposition(variant[field]));
    if (compositions.some((composition) => !composition || !composition.length)) {
      return { provided: true, valid: false, proteins: [] };
    }
    const first = compositions[0];
    if (compositions.some((composition) => !sameProteinSet(composition, first))) {
      return { provided: true, valid: false, proteins: [] };
    }
    return { provided: true, valid: true, proteins: first };
  }

  function parseExactBoxSizeLabel(value) {
    const label = normaliseVariantLabel(value);
    const match = /^(\d+)\s*lb$/.exec(label);
    if (!match) return { kind: "not_size" };
    const size = Number(match[1]);
    return BOX_SIZES.includes(size)
      ? { kind: "size", size }
      : { kind: "invalid", reason: "unknown_box_size" };
  }

  function variantOptionValues(variant) {
    return unique([
      ...asArray(variant.options),
      variant.option1,
      variant.option2,
      variant.option3,
    ].map(normaliseVariantLabel).filter(Boolean));
  }

  function compositionIsAvailableAtSize(label, size) {
    if (label === "10 lb beef / 10 lb turkey / 10 lb turkey & salmon") return size === 30;
    const isListedVarietyOption = Object.values(VARIETY_OPTION_LABELS_BY_SIZE).some((labels) => labels.includes(label));
    if (isListedVarietyOption) return (VARIETY_OPTION_LABELS_BY_SIZE[size] || []).includes(label);
    return true;
  }

  function titleMatchesActualLabels(title, size, compositionLabel) {
    const value = normaliseVariantLabel(title);
    if (!value) return true;
    const sizeLabel = `${size} lb`;
    return [
      sizeLabel,
      compositionLabel,
      `${sizeLabel} / ${compositionLabel}`,
      `${compositionLabel} / ${sizeLabel}`,
    ].includes(value);
  }

  /**
   * Resolve the selected variant rather than trusting the product handle. The
   * exact authoritative size and composition labels are required. A matching
   * metadata field can corroborate those labels but can never replace or
   * contradict them. Unknown extra option values fail closed.
   */
  function resolveVariantComposition(variant) {
    if (!isPlainObject(variant)) return { valid: false, proteins: [], source: "missing_variant", reason: "A selected variant is required." };
    const options = variantOptionValues(variant);
    if (!options.length) {
      return { valid: false, proteins: [], source: "missing_variant_options", reason: "The selected variant does not expose authoritative size and composition options." };
    }

    const sizes = new Set();
    const compositionLabels = new Set();
    const unknown = [];
    for (const option of options) {
      const size = parseExactBoxSizeLabel(option);
      if (size.kind === "size") {
        sizes.add(size.size);
        continue;
      }
      if (size.kind === "invalid") {
        unknown.push(option);
        continue;
      }
      if (hasOwn(CANONICAL_VARIANT_COMPOSITIONS, option)) {
        compositionLabels.add(option);
      } else {
        unknown.push(option);
      }
    }
    if (unknown.length) {
      return { valid: false, proteins: [], source: "unknown_variant_option", reason: "The selected variant includes an unknown size or protein-composition option." };
    }
    if (sizes.size !== 1) {
      return { valid: false, proteins: [], source: sizes.size ? "ambiguous_variant_size" : "missing_variant_size", reason: "The selected variant must expose exactly one authoritative box-size option." };
    }
    if (compositionLabels.size !== 1) {
      return { valid: false, proteins: [], source: compositionLabels.size ? "ambiguous_variant_composition" : "missing_variant_composition", reason: "The selected variant must expose exactly one authoritative full protein-composition option." };
    }

    const size = [...sizes][0];
    const compositionLabel = [...compositionLabels][0];
    const proteins = [...CANONICAL_VARIANT_COMPOSITIONS[compositionLabel]];
    if (!compositionIsAvailableAtSize(compositionLabel, size)) {
      return { valid: false, proteins: [], source: "composition_not_available_at_selected_size", reason: "That protein-composition option is not an approved actual option for the selected box size." };
    }
    if (!titleMatchesActualLabels(variant.title, size, compositionLabel)) {
      return { valid: false, proteins: [], source: "variant_title_disagrees", reason: "The selected variant title does not match its authoritative size and protein-composition options." };
    }

    const metadata = explicitCompositionMetadata(variant);
    if (!metadata.valid) {
      return { valid: false, proteins: [], source: "invalid_explicit_composition", reason: "The selected variant includes invalid or conflicting explicit protein metadata." };
    }
    if (metadata.provided && !sameProteinSet(metadata.proteins, proteins)) {
      return { valid: false, proteins: [], source: "explicit_metadata_disagrees", reason: "The selected variant's explicit protein metadata disagrees with its authoritative option labels." };
    }
    return {
      valid: true,
      proteins,
      size,
      compositionLabel,
      matchedLabel: compositionLabel,
      source: metadata.provided ? "authoritative_option_labels_with_verified_metadata" : "authoritative_option_labels",
      reason: null,
    };
  }

  function sameProteinSet(left, right) {
    if (left.length !== right.length) return false;
    const rightSet = new Set(right);
    return left.every((protein) => rightSet.has(protein));
  }

  function variantMatchesSize(variant, size) {
    const target = Number(size);
    if (!BOX_SIZES.includes(target)) return false;
    const composition = resolveVariantComposition(variant);
    return composition.valid && composition.size === target;
  }

  function hasVerifiedVariantIdentity(variant) {
    return isPlainObject(variant) && Boolean(toText(variant.id)) && variant.available === true;
  }

  /**
   * Pick one concrete, available variant for a calculated role and box size.
   * The returned variant is still required to pass validateCartLines immediately
   * before /cart/add; selection is not a substitute for the final cart gate.
   */
  function selectRecommendedVariant(role, size, variants, avoidances) {
    const excluded = new Set(normaliseAvoidances(avoidances));
    const expected = ROLE_PROTEINS[role];
    if (!expected) return { valid: false, variant: null, proteinComposition: [], reason: "The requested recommendation role is not recognised." };
    const candidates = asArray(variants).map((variant) => {
      const composition = resolveVariantComposition(variant);
      return { variant, composition };
    }).filter(({ variant, composition }) => {
      if (!hasVerifiedVariantIdentity(variant) || !composition.valid || !variantMatchesSize(variant, size)) return false;
      if (composition.proteins.some((protein) => excluded.has(protein))) return false;
      if (role === "variety") return composition.proteins.length >= 2;
      return sameProteinSet(composition.proteins, expected);
    }).sort((left, right) => {
      if (role !== "variety") return 0;
      return right.composition.proteins.length - left.composition.proteins.length;
    });
    if (!candidates.length) {
      return { valid: false, variant: null, proteinComposition: [], reason: "No available variant of this size can be verified as compatible with the selected proteins." };
    }
    return {
      valid: true,
      variant: candidates[0].variant,
      proteinComposition: candidates[0].composition.proteins,
      compositionSource: candidates[0].composition.source,
      compositionLabel: candidates[0].composition.compositionLabel,
      reason: null,
    };
  }

  /**
   * Mandatory final cart gate. It verifies availability, concrete variant
   * composition, each hard avoidance, selected role integrity, and (when
   * supplied) selling-plan allocation before a request can be sent.
   */
  function normaliseBoxPlanQuantities(boxPlan) {
    if (!isPlainObject(boxPlan) || !isPlainObject(boxPlan.quantities)) {
      return { valid: false, quantities: {} };
    }
    const quantities = {};
    for (const [rawSize, rawQuantity] of Object.entries(boxPlan.quantities)) {
      const size = Number(rawSize);
      const quantity = Number(rawQuantity);
      if (!BOX_SIZES.includes(size) || !Number.isInteger(quantity) || quantity < 0) {
        return { valid: false, quantities: {} };
      }
      if (quantity > 0) quantities[size] = quantity;
    }
    return { valid: Object.keys(quantities).length > 0, quantities };
  }

  function sameBoxPlanQuantities(left, right) {
    return BOX_SIZES.every((size) => Number(left[size] || 0) === Number(right[size] || 0));
  }

  function requestedSubscriptionCadenceWeeks(result) {
    if (!isPlainObject(result)) return 4;
    const explicit = result.purchaseCadenceWeeks
      ?? result.purchase_cadence_weeks
      ?? result.cadenceWeeks
      ?? result.cadence_weeks
      ?? (result.cadenceSellingPlan && result.cadenceSellingPlan.cadenceWeeks);
    return explicit === undefined || explicit === null || explicit === ""
      ? 4
      : normaliseCadenceWeeks(explicit);
  }

  function cartBoxPlanForCadence(result, cadenceWeeks) {
    if (!isPlainObject(result)) return null;
    if (cadenceWeeks === 2) return result.cadenceBoxPlan || result.cadence_box_plan || null;
    return result.boxPlan || null;
  }

  function cartSellingPlanForCadence(result, cadenceWeeks) {
    if (!isPlainObject(result)) return null;
    const candidate = cadenceWeeks === 2
      ? (result.cadenceSellingPlan || result.cadence_selling_plan)
      : result.sellingPlan;
    if (planIsEveryCadenceWeeks(candidate, cadenceWeeks)) return candidate;
    // Mixed box sizes can have distinct allocated IDs. commonCadencePlan then
    // has no single ID; verify its aggregate metadata/map here, while the cart
    // loop still requires each ID to exist on its exact variant and cadence.
    if (!isPlainObject(candidate) || candidate.id !== null
      || normaliseCadenceWeeks(candidate.cadenceWeeks) !== cadenceWeeks
      || toText(candidate.name) !== `Every ${cadenceWeeks} Weeks`
      || !isPlainObject(candidate.linePlanIds)) return null;
    const expectedPlan = normaliseBoxPlanQuantities(cartBoxPlanForCadence(result, cadenceWeeks));
    return expectedPlan.valid && Object.keys(expectedPlan.quantities)
      .every((size) => Boolean(toText(candidate.linePlanIds[size]))) ? candidate : null;
  }

  function validateCartLines(lines, avoidances, result) {
    const violations = [];
    const safeLines = [];
    const rawAvoidances = avoidanceSelection(avoidances);
    // Reapply the reviewed breed rule independently of a cached restriction
    // list, so replacing a result variant cannot erase its Beef exclusion.
    const excluded = new Set(getEffectiveAvoidances(avoidances, result));
    if (rawAvoidances.unknown.length) {
      return { valid: false, reason: "The protein restrictions could not be verified.", violations: ["unknown_protein_avoidance"], lines: [] };
    }
    if (result && result.purchaseAllowed === false) {
      return { valid: false, reason: "The result is not eligible for cart addition.", violations: ["result_not_purchasable"], lines: [] };
    }
    if (!Array.isArray(lines) || !lines.length) {
      return { valid: false, reason: "No product variants were selected.", violations: ["missing_cart_lines"], lines: [] };
    }
    const purchaseType = result ? normalisePurchaseType(result.purchaseType || result.purchase_type) : null;
    const cadenceWeeks = purchaseType === "subscription" ? requestedSubscriptionCadenceWeeks(result) : null;
    const expectedBoxPlan = result && purchaseType === "subscription"
      ? cartBoxPlanForCadence(result, cadenceWeeks)
      : (result && result.boxPlan ? result.boxPlan : null);
    const expectedPlan = expectedBoxPlan ? normaliseBoxPlanQuantities(expectedBoxPlan) : null;
    if (expectedPlan && !expectedPlan.valid) {
      return { valid: false, reason: "The calculated box plan is not valid for cart verification.", violations: ["invalid_box_plan"], lines: [] };
    }
    if (result && purchaseType === "subscription" && result.subscriptionPlanStatus === "verified") {
      if (!cadenceWeeks) {
        return { valid: false, reason: "The selected subscription cadence cannot be verified.", violations: ["selling_plan_cadence_unverified"], lines: [] };
      }
      if (cadenceWeeks === 2 && (!expectedBoxPlan || !cartSellingPlanForCadence(result, cadenceWeeks))) {
        return { valid: false, reason: "The selected two-week shipment plan cannot be verified.", violations: ["selling_plan_cadence_unverified"], lines: [] };
      }
    }
    const actualQuantities = {};
    for (const line of lines) {
      const item = isPlainObject(line) ? line : { variant: line };
      const variant = item.variant || item;
      const quantity = Number(item.quantity === undefined ? 1 : item.quantity);
      if (!Number.isInteger(quantity) || quantity <= 0) {
        violations.push("invalid_quantity");
        continue;
      }
      if (!isPlainObject(variant) || !toText(variant.id)) {
        violations.push("missing_variant_id");
        continue;
      }
      if (variant.available !== true) {
        violations.push("variant_availability_unverified");
        continue;
      }
      const composition = resolveVariantComposition(variant);
      if (!composition.valid) {
        violations.push(composition.source);
        continue;
      }
      if (item.size !== undefined && Number(item.size) !== composition.size) {
        violations.push("declared_variant_size_mismatch");
        continue;
      }
      actualQuantities[composition.size] = (actualQuantities[composition.size] || 0) + quantity;
      const avoided = composition.proteins.filter((protein) => excluded.has(protein));
      if (avoided.length) {
        violations.push(`avoided_protein:${avoided.join(",")}`);
        continue;
      }
      if (result && result.productRole && result.productRole !== "variety") {
        const expected = ROLE_PROTEINS[result.productRole] || [];
        if (!sameProteinSet(composition.proteins, expected)) {
          violations.push("variant_does_not_match_result");
          continue;
        }
      }
      if (result && result.productRole === "variety" && composition.proteins.length < 2) {
        violations.push("variety_variant_not_mixed");
        continue;
      }
      const sellingPlanId = item.sellingPlanId || item.selling_plan;
      const expectedSelection = result && asArray(result.variantSelections).find((selection) => (
        Number(selection && selection.size) === composition.size
        && (!selection.variantId || String(selection.variantId) === String(variant.id))
      ));
      const cadencePlan = purchaseType === "subscription"
        ? cartSellingPlanForCadence(result, cadenceWeeks)
        : null;
      const requiredPlanId = result && result.subscriptionPlanStatus === "verified"
        ? toText(cadencePlan && cadencePlan.linePlanIds && cadencePlan.linePlanIds[String(composition.size)])
          || toText(cadencePlan && cadencePlan.id)
          || (cadenceWeeks === 4 ? toText(expectedSelection && expectedSelection.sellingPlanId) : "")
        : null;
      if (purchaseType === "subscription" && result && result.subscriptionPlanStatus === "verified") {
        if (!sellingPlanId) {
          violations.push("selling_plan_required");
          continue;
        }
      }
      if (purchaseType === "one_time" && sellingPlanId) {
        violations.push("selling_plan_not_allowed_for_one_time");
        continue;
      }
      const planMismatch = purchaseType === "subscription"
        && result
        && result.subscriptionPlanStatus === "verified"
        && (!requiredPlanId || String(sellingPlanId) !== requiredPlanId);
      if (planMismatch) violations.push("selling_plan_mismatch");
      if (sellingPlanId) {
        const allocatedPlan = asArray(variant.sellingPlans)
          .find((plan) => String(plan && plan.id) === String(sellingPlanId));
        if (!allocatedPlan) {
          violations.push("selling_plan_not_allocated");
          continue;
        }
        if (purchaseType === "subscription" && result && result.subscriptionPlanStatus === "verified") {
          if (!planIsEveryCadenceWeeks(allocatedPlan, cadenceWeeks)) violations.push("selling_plan_cadence_mismatch");
          if (planMismatch || !planIsEveryCadenceWeeks(allocatedPlan, cadenceWeeks)) continue;
        }
      }
      safeLines.push({ ...item, variant, size: composition.size, quantity, proteinComposition: composition.proteins, compositionSource: composition.source });
    }
    if (expectedPlan && !sameBoxPlanQuantities(expectedPlan.quantities, actualQuantities)) {
      violations.push("box_plan_size_quantity_mismatch");
    }
    if (violations.length || safeLines.length !== lines.length) {
      return { valid: false, reason: "One or more selected variants cannot be verified as safe for this plan.", violations: unique(violations), lines: safeLines };
    }
    return { valid: true, reason: null, violations: [], lines: safeLines };
  }

  function fallbackUuidV4() {
    const bytes = Array.from({ length: 16 }, () => Math.floor(Math.random() * 256));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = bytes.map((byte) => byte.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  function isUuid(value) {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(toText(value));
  }

  function opaqueId(prefix, options) {
    const config = isPlainObject(options) ? options : {};
    if (typeof config.idFactory === "function") return String(config.idFactory(prefix));
    const cryptographic = typeof globalThis !== "undefined" && globalThis.crypto && typeof globalThis.crypto.randomUUID === "function"
      ? globalThis.crypto.randomUUID()
      : fallbackUuidV4();
    return cryptographic;
  }

  /**
   * These helpers assign the generated opaque identifiers onto the passed state
   * so a resume/retry cannot generate a second dog or completion record.
   */
  function ensureDogId(state, options) {
    const target = isPlainObject(state) ? state : {};
    const existing = toText(target.dogId || target.dog_id);
    const config = isPlainObject(options) ? options : {};
    const dogId = (existing && (isUuid(existing) || typeof config.idFactory === "function")) ? existing : opaqueId("dog", config);
    target.dogId = dogId;
    target.dog_id = dogId;
    return dogId;
  }

  function ensureCompletionId(state, options) {
    const target = isPlainObject(state) ? state : {};
    const existing = toText(target.completionId || target.completion_id);
    const config = isPlainObject(options) ? options : {};
    if (existing && (isUuid(existing) || typeof config.idFactory === "function")) {
      target.completionId = existing;
      target.completion_id = existing;
      return existing;
    }
    ensureDogId(target, config);
    const completionId = opaqueId("completion", config);
    target.completionId = completionId;
    target.completion_id = completionId;
    return completionId;
  }

  function consentState(state, options) {
    const stateSource = isPlainObject(state) ? state : {};
    const source = isPlainObject(state && state.consent) ? state.consent : {};
    const config = isPlainObject(options) ? options : {};
    const override = isPlainObject(config.consent) ? config.consent : {};
    const resolve = (key, legacyKey) => {
      if (hasOwn(override, key)) return override[key] === true;
      if (hasOwn(config, legacyKey)) return config[legacyKey] === true;
      if (hasOwn(source, key)) return source[key] === true;
      return stateSource[legacyKey] === true;
    };
    return {
      analytics: resolve("analytics", "analyticsConsent"),
      marketing: resolve("marketing", "marketingConsent"),
      transactional: resolve("transactional", "transactionalConsent"),
    };
  }

  function qualificationCategory(result) {
    if (result && toText(result.qualificationCategory)) return result.qualificationCategory;
    if (result && result.status === "no_sale") return "no_compatible_product";
    if (result && result.status === "conditional") return "conditional_review";
    if (result && result.status === "wait") return "waiting_for_readiness";
    return "standard_adult";
  }

  function lifecycleDogData(state, result, dogId, options) {
    const config = isPlainObject(options) ? options : {};
    const profile = result && result.breedProfile
      ? result.breedProfile
      : rehydrateBreedSelection(state.breed, config.reviewedBreeds || config.breedCatalog || config.breeds, state.currentWeight, config.taxonomy);
    const age = result && result.age ? result.age : deriveAge(state, profile, { asOf: state.ageAsOf || DEFAULT_AS_OF() });
    const birthday = validateBirthday(state, { asOf: age.ageAsOf || state.ageAsOf || DEFAULT_AS_OF() });
    const dog = {
      dog_id: dogId,
      dog_name: formatDogName(state.name),
      breed: profile.name,
      birthday_precision: birthday.birthdayPrecision,
      age_as_of: age.ageAsOf || null,
      next_eligible_date: age.nextEligibleDate || null,
      next_eligible_date_precision: age.nextEligibleDatePrecision || "unknown",
      qualification_category: qualificationCategory(result),
      recommendation: result && result.productRole ? result.productRole : "none",
    };
    if (birthday.birthdayPrecision === "exact_day") dog.birthday = birthday.birthday;
    if (birthday.birthdayPrecision === "month_only") dog.birthday_month = birthday.birthMonth;
    if (birthday.birthdayPrecision === "approximate_age") dog.estimated_age_months = birthday.estimatedAgeMonths;
    return dog;
  }

  function consentMetadata(source, config, consent) {
    return {
      marketing: consent.marketing === true,
      email_marketing: consent.marketing === true,
      source: toText(config.consentSource || source.consentSource || "raw_fit_quiz_send_my_results"),
      wording_version: toText(config.consentWordingVersion || source.consentWordingVersion || "raw-fit-v3-email-marketing") || "raw-fit-v3-email-marketing",
      timestamp: toText(config.consentTimestamp || source.consentTimestamp || config.completedAt || new Date().toISOString()),
    };
  }

  function codedQuizSnapshot(state, result, dogId, completionId, config) {
    const source = isPlainObject(state) ? state : {};
    const resolved = result || {};
    const dog = lifecycleDogData(source, resolved, dogId, config);
    const conditions = normaliseConditionSelection(source.health);
    const tolerated = normaliseToleratedProtein(source.toleratedProtein || source.tolerated_protein || source.knownToleratedProtein || source.known_tolerated_protein);
    const boxPlan = isPlainObject(resolved.boxPlan) ? resolved.boxPlan : null;
    const snapshotNumber = (value) => (
      value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value))
        ? Number(value)
        : null
    );
    const requiredPounds = snapshotNumber(resolved.fourWeekPounds ?? (boxPlan && (boxPlan.requiredPounds28Days ?? boxPlan.requiredPounds)));
    const deliveredPounds = snapshotNumber(boxPlan && (boxPlan.deliveredPounds ?? boxPlan.total));
    const calculatedDifference = requiredPounds !== null && deliveredPounds !== null
      ? deliveredPounds - requiredPounds
      : null;
    const quantities = boxPlan && isPlainObject(boxPlan.quantities) ? {
      box_quantities: {
        8: Number(boxPlan.quantities[8] || 0),
        14: Number(boxPlan.quantities[14] || 0),
        30: Number(boxPlan.quantities[30] || 0),
      },
      box_8_lb: Number(boxPlan.quantities[8] || 0),
      box_14_lb: Number(boxPlan.quantities[14] || 0),
      // Historical schema-3.0 compatibility only; never map 14 to 16.
      box_16_lb: 0,
      box_30_lb: Number(boxPlan.quantities[30] || 0),
      total_ordered_pounds: deliveredPounds,
      delivered_pounds: deliveredPounds,
      coverage_days: snapshotNumber(boxPlan.coverageDays),
      buffer_pounds: snapshotNumber(boxPlan.bufferPounds),
      overage_pounds: snapshotNumber(boxPlan.overagePounds) ?? (calculatedDifference !== null ? Math.max(0, calculatedDifference) : null),
      shortfall_pounds: snapshotNumber(boxPlan.shortfallPounds) ?? (calculatedDifference !== null ? Math.max(0, calculatedDifference * -1) : null),
      days_not_covered: snapshotNumber(boxPlan.daysNotCovered),
      // The email step is intentionally before the results-page purchase
      // controls. Snapshot the package recommendation, never a later mode.
      recommended_cadence_weeks: Number(resolved.recommendedCadenceWeeks) === 2 ? 2 : 4,
      recommended_purchase_mode: "subscription",
    } : null;
    return {
      schema_version: SCHEMA_VERSION,
      quiz_version: QUIZ_VERSION,
      calculation_revision: CALCULATION_REVISION,
      completion_id: completionId,
      $event_id: completionId,
      dog_id: dogId,
      dog,
      human_first_name: toText(config.firstName || source.firstName || source.humanFirstName) || null,
      dog_name: dog.dog_name,
      dog_breed: dog.breed,
      birthday_precision: dog.birthday_precision,
      birthday: dog.birthday || null,
      birthday_month: dog.birthday_month || null,
      estimated_age_months: dog.estimated_age_months ?? null,
      age_as_of: dog.age_as_of,
      current_weight_lb: validWeight(source.currentWeight),
      body_condition: normaliseBodyCondition(source.bodyCondition || source.body_condition, source.goal),
      weight_goal: ({ underweight: "gain", ideal: "maintain", overweight: "lose" })[normaliseBodyCondition(source.bodyCondition || source.body_condition, source.goal)] || "maintain",
      // This field now names the actual weight used by the estimate. It is not
      // a customer target and never copies a stale targetWeight value.
      target_weight_lb: validWeight(source.currentWeight),
      activity: normaliseActivity(source.activity),
      raw_experience: normaliseRawExperience(source.rawStatus || source.raw_experience),
      avoidances: normaliseAvoidances(source.proteins),
      effective_avoidances: getEffectiveAvoidances(source.proteins, resolved),
      diet_watch_flags: asArray(resolved.dietWatchFlags),
      breed_notices: asArray(resolved.breedNotices).map(({ id, title, message }) => ({ id, title, message })),
      transition_plan: buildTransitionPlan(source),
      tolerated_protein: Array.isArray(tolerated) && tolerated.length === 1 ? tolerated[0] : tolerated,
      tolerated_proteins: Array.isArray(tolerated) ? tolerated : [],
      conditions,
      condition_timing: resolved.conditionState ? {
        status: resolved.conditionState.status,
        wait: resolved.conditionState.waitFlags,
        conditional: resolved.conditionState.conditionalFlags,
      } : null,
      primary_outcome: primaryOutcome(source) || null,
      outcomes: normaliseSelection(source.outcomes, "none").filter((value) => value !== "none"),
      feeding_status: resolved.status || "no_sale",
      status_reason_codes: asArray(resolved.holds),
      recommendation_route: resolved.recommendationRoute || resolved.route || null,
      primary_recommendation: resolved.productRole || null,
      secondary_recommendation: resolved.alternativeRole || null,
      recommendation_reasons: asArray(resolved.reasons).filter((reason) => !asArray(resolved.breedNotices).some((notice) => notice.message === reason)),
      daily_ounces: Number.isFinite(resolved.dailyOz) ? resolved.dailyOz : null,
      meals_per_day: Number.isFinite(resolved.mealsPerDay) ? resolved.mealsPerDay : null,
      per_meal_ounces: Number.isFinite(resolved.perMealOz) ? resolved.perMealOz : null,
      required_pounds_28_days: requiredPounds,
      order_plan: quantities,
      pricing: resolved.pricing ? {
        currency: resolved.pricing.currency,
        one_time_price_cents: resolved.pricing.oneTimePriceCents,
        subscription_price_cents: resolved.pricing.subscriptionPriceCents,
        subscription_savings_cents: resolved.pricing.savingsCents,
        subscription_savings_percent: resolved.pricing.savingsPercent,
      } : null,
      recommended_cadence_weeks: quantities ? quantities.recommended_cadence_weeks : null,
      recommended_purchase_mode: quantities ? quantities.recommended_purchase_mode : null,
      purchase_type: quantities ? quantities.recommended_purchase_mode : null,
      selling_plan_id: resolved.sellingPlan && resolved.sellingPlan.id ? String(resolved.sellingPlan.id) : null,
      selling_plan_ids: resolved.sellingPlan && isPlainObject(resolved.sellingPlan.linePlanIds)
        ? { ...resolved.sellingPlan.linePlanIds }
        : null,
      selling_plan_cadence_weeks: resolved.sellingPlan ? Number(resolved.sellingPlan.cadenceWeeks || 4) : null,
      subscription_plan_status: resolved.subscriptionPlanStatus || "unavailable",
      completed_at: config.completedAt || new Date().toISOString(),
    };
  }

  function buildCompletionPayload(state, result, options) {
    const source = isPlainObject(state) ? state : createDefaultState();
    const config = isPlainObject(options) ? options : {};
    const resolved = result || calculateResult(source, { ...config, asOf: config.asOf || source.ageAsOf || DEFAULT_AS_OF() });
    const dogId = ensureDogId(source, config);
    const completionId = ensureCompletionId(source, config);
    const consent = consentState(source, config);
    const email = toText(config.email || source.email).toLowerCase();
    const emailValid = /^\S+@\S+\.\S+$/.test(email);
    const explicitSend = config.sendMyResults === true || config.identificationConfirmed === true;
    const shouldTransmit = explicitSend && emailValid && consent.marketing === true;
    const base = {
      eventName: "Raw Fit Quiz Completed",
      event_name: "Raw Fit Quiz Completed",
      schema_version: SCHEMA_VERSION,
      completion_id: completionId,
      $event_id: completionId,
      dog_id: dogId,
      idempotencyKey: `quiz-completed:${completionId}`,
      localOnly: !shouldTransmit,
      shouldTransmit,
      email: emailValid ? email : null,
      consent: { ...consentMetadata(source, config, consent), send_results: explicitSend },
    };
    if (!shouldTransmit) return { ...base, properties: null };
    const snapshot = isPlainObject(config.completionSnapshot)
      ? clone(config.completionSnapshot)
      : codedQuizSnapshot(source, resolved, dogId, completionId, config);
    return { ...base, properties: { ...snapshot, completion_snapshot: snapshot } };
  }

  function buildPlanEmailPayload(state, result, options) {
    const source = isPlainObject(state) ? state : createDefaultState();
    const config = isPlainObject(options) ? options : {};
    const resolved = result || calculateResult(source, { ...config, asOf: config.asOf || source.ageAsOf || DEFAULT_AS_OF() });
    const email = toText(config.email || source.email).toLowerCase();
    const emailValid = /^\S+@\S+\.\S+$/.test(email);
    const dogId = ensureDogId(source, config);
    const completionId = ensureCompletionId(source, config);
    const consent = consentState(source, config);
    const acknowledged = config.providerAcknowledged === true;
    const failed = Boolean(config.providerError) && !acknowledged;
    const providerState = !emailValid ? "invalid" : (acknowledged ? "acknowledged" : (failed ? "failed" : "pending"));
    const marketing = hasOwn(config, "marketingConsent") ? config.marketingConsent === true : consent.marketing;
    const consented = { ...consent, marketing };
    const shouldTransmit = emailValid && marketing === true;
    return {
      eventName: "Raw Fit Plan Requested",
      event_name: "Raw Fit Plan Requested",
      schema_version: SCHEMA_VERSION,
      completion_id: completionId,
      $event_id: completionId,
      dog_id: dogId,
      idempotencyKey: `plan-email:${completionId}:${dogId}`,
      shouldTransmit,
      email: emailValid ? email : null,
      consent: { ...consentMetadata(source, config, consented), transactional: shouldTransmit, send_results: true, email_marketing: marketing },
      providerState,
      planSaved: providerState === "acknowledged",
      retryable: providerState === "failed",
      providerError: failed ? "We could not confirm delivery. Please try again." : null,
      properties: shouldTransmit ? (() => {
        const snapshot = isPlainObject(config.completionSnapshot)
          ? clone(config.completionSnapshot)
          : codedQuizSnapshot(source, resolved, dogId, completionId, config);
        return { ...snapshot, completion_snapshot: snapshot };
      })() : null,
    };
  }

  function buildSmsOptInPayload(state, result, options) {
    const source = isPlainObject(state) ? state : createDefaultState();
    const config = isPlainObject(options) ? options : {};
    const phone = toText(config.phone || source.phone);
    const smsConsent = config.smsMarketingConsent === true || source.smsMarketingConsent === true;
    const dogId = ensureDogId(source, config);
    const completionId = ensureCompletionId(source, config);
    const shouldTransmit = Boolean(phone) && smsConsent;
    return {
      eventName: "Raw Fit SMS Opt-In",
      event_name: "Raw Fit SMS Opt-In",
      schema_version: SCHEMA_VERSION,
      completion_id: completionId,
      $event_id: completionId,
      dog_id: dogId,
      idempotencyKey: `raw-fit-sms:${completionId}`,
      shouldTransmit,
      phone: shouldTransmit ? phone : null,
      consent: {
        sms_marketing: smsConsent,
        source: toText(config.consentSource || source.smsConsentSource || "raw_fit_quiz_results"),
        wording_version: toText(config.consentWordingVersion || source.smsConsentWordingVersion || "raw-fit-v3-sms") || "raw-fit-v3-sms",
        timestamp: toText(config.consentTimestamp || source.smsConsentTimestamp || new Date().toISOString()),
      },
      properties: shouldTransmit ? {
        schema_version: SCHEMA_VERSION,
        completion_id: completionId,
        $event_id: completionId,
        dog_id: dogId,
        phone_opt_in: true,
        feeding_status: result && result.status ? result.status : null,
      } : null,
    };
  }

  return {
    SCHEMA_VERSION,
    QUIZ_VERSION,
    TOTAL_STEPS,
    INTERSTITIALS: clone(INTERSTITIALS),
    CHAPTER_CARDS: clone(CHAPTER_CARDS),
    REVIEWED_BREED_GROUPS: { ...REVIEWED_BREED_GROUPS },
    CALCULATION_REVISION,
    FORMULA_CATALOG: clone(FORMULA_CATALOG),
    ROLE_PROTEINS: clone(ROLE_PROTEINS),
    CANONICAL_VARIANT_COMPOSITIONS: clone(CANONICAL_VARIANT_COMPOSITIONS),
    createDefaultState,
    migrateState,
    validateBirthday,
    deriveAge,
    deriveBreedProfile,
    evaluateQuestionState,
    rehydrateBreedSelection,
    resolveBreedRules,
    getEffectiveAvoidances,
    buildTransitionPlan,
    normalizeExclusiveSelection,
    resolveFormulaCatalog,
    selectInterstitial,
    selectChapterCard,
    calculateResult,
    calculateBoxPlan,
    calculateCadenceBoxPlan,
    recommendCadenceWeeks,
    calculateFeedingPlan: feedingPlan,
    resolveVariantComposition,
    selectRecommendedVariant,
    selectRecommendedSubscriptionVariant,
    planIsEveryCadenceWeeks,
    plansForCadenceForVariant,
    commonCadencePlan,
    verifyCadenceBoxPlanInventory,
    fourWeekPlansForVariant,
    validateCartLines,
    ensureDogId,
    ensureCompletionId,
    buildCompletionPayload,
    buildPlanEmailPayload,
    buildSmsOptInPayload,
  };
}));
